import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import moment from 'moment';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import DateTimePicker from 'react-datetime';
import { ItemAvailability, LevelRewardInput, LevelRewardInputType } from '../../../../../__gqltypes__';
import {
  CREATE_LOYALTY_CAMPAIGN_CARD,
  DELETE_LOYALTY_CAMPAIGN_CARD,
  DELETE_LOYALTY_CAMPAIGN_CONFIG,
  GET_LOYALTY_CAMPAIGN_ADMIN,
  GET_LOYALTY_CAMPAIGN_SKU_OPTIONS,
  UPDATE_LOYALTY_CAMPAIGN_CARD,
  UPSERT_LOYALTY_CAMPAIGN_CONFIG,
} from './graphql';
import EditRewardsModal from '../../components/EditRewardsModal';
import { LoadingLogo } from '../../../devtools/components/Modal';
import CardModal from './components/CardModal';
import ConfigModal from './components/ConfigModal';
import {
  ConfigModalMode,
  LOYALTY_CAMPAIGN_ITEM_CATEGORY_LABELS,
  LoyaltyCampaignCardKind,
  LoyaltyCampaignCardRow,
  LoyaltyCampaignCardSectionId,
  LoyaltyCampaignCardType,
  LoyaltyCampaignClaimMode,
  LoyaltyCampaignConfigRow,
  LoyaltyCampaignItemCategory,
  LoyaltyCampaignOfferOptionRow,
  LoyaltyCampaignSkuPriceRow,
  RewardFormInput,
  SavedRewardRow,
  VanityItemType,
  VANITY_ITEM_LABELS,
} from './types';
import {
  buildOfferPriceMap,
  buildOfferSkuMap,
  buildSkuPriceMap,
  findOfferIdBySku,
  formatOfferOptionLabel,
  formatCompactNumber,
  formatSavedReward,
  fromLevelRewardInputToRewardForm,
  fromRewardFormToLevelRewardInput,
  getAllowedRewardTypesForItemCategory,
  getCardClaimMode,
  getCardClaimType,
  getItemCategoryBadgeStyle,
  getItemCategoryFromReward,
  getItemCategoryFromRewardForm,
  getLoyaltyPointsByPrice,
  getOfferPriceUSD,
  getRewardLabel,
  getSkuPriceUSD,
  getVanityItemTypeFromReward,
  getVanityItemTypeFromRewardForm,
  parseSkus,
  scrollToSection,
  toRewardFormInput,
  toTitleLabel,
} from './utils';

const DRAFT_CARD_ID_PREFIX = 'draft-card:';
const DRAFT_GROUP_ID_PREFIX = 'draft-group:';
const ITEM_CATEGORY_ORDER: LoyaltyCampaignItemCategory[] = ['COINS', 'DIAMONDS', 'BOOSTS', 'FRAGMENTS', 'VANITY', 'OTHER'];
const CARD_SECTION_IDS: LoyaltyCampaignCardSectionId[] = [
  'bundle',
  'item-coins',
  'item-diamonds',
  'item-boosts',
  'item-fragments',
  'item-vanity',
  'item-other',
];
const SHOULD_SKIP_GENERIC_OFFER_LABEL = (offerId: string): boolean => /^diamond\.\d+$/.test(offerId);
type CardLifecycleState = 'scheduled' | 'active' | 'ended';
const PAGE_MAX_WIDTH = 1120;
const SECTION_MAX_WIDTH = 980;

const pageShellStyle = {
  maxWidth: PAGE_MAX_WIDTH,
  margin: '0 auto',
  paddingBottom: 120,
};

const sectionCardStyle = {
  borderRadius: 20,
  border: '1px solid #e3e7eb',
  boxShadow: '0 14px 40px rgba(15, 23, 42, 0.06)',
  overflow: 'hidden' as const,
};

const sectionHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 16,
  marginBottom: 20,
  flexWrap: 'wrap' as const,
};

const sectionEyebrowStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '6px 12px',
  borderRadius: 999,
  background: '#eef4ff',
  color: '#2b5fb8',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase' as const,
};

const metaChipStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '8px 12px',
  borderRadius: 999,
  background: '#f6f8fb',
  border: '1px solid #e2e8f0',
  color: '#334155',
  fontSize: 13,
  fontWeight: 600,
};

const rewardChipStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '8px 12px',
  borderRadius: 999,
  background: '#edf7ff',
  border: '1px solid #bfdbfe',
  color: '#0f4c81',
  fontSize: 13,
  fontWeight: 600,
};

const ITEM_CATEGORY_CARD_STYLES: Record<
  LoyaltyCampaignItemCategory,
  {
    borderColor: string;
    background: string;
    badgeBackground: string;
    badgeText: string;
    headerBackground: string;
    headerBorderColor: string;
  }
> = {
  COINS: {
    borderColor: '#f5d78e',
    background: '#fffdf4',
    badgeBackground: '#b7791f',
    badgeText: '#fffaf0',
    headerBackground: '#fff3cd',
    headerBorderColor: '#f6c453',
  },
  DIAMONDS: {
    borderColor: '#b9d9ff',
    background: '#f7fbff',
    badgeBackground: '#2563eb',
    badgeText: '#eff6ff',
    headerBackground: '#d9efff',
    headerBorderColor: '#79e3ff',
  },
  BOOSTS: {
    borderColor: '#c8efd8',
    background: '#f7fdf9',
    badgeBackground: '#15803d',
    badgeText: '#f0fdf4',
    headerBackground: '#ffe0b8',
    headerBorderColor: '#ffb454',
  },
  FRAGMENTS: {
    borderColor: '#b7e7c6',
    background: '#f5fdf7',
    badgeBackground: '#15803d',
    badgeText: '#f0fdf4',
    headerBackground: '#dcfce7',
    headerBorderColor: '#86efac',
  },
  VANITY: {
    borderColor: '#e7cdf9',
    background: '#fdf9ff',
    badgeBackground: '#9333ea',
    badgeText: '#faf5ff',
    headerBackground: '#ead7ff',
    headerBorderColor: '#d6b0ff',
  },
  OTHER: {
    borderColor: '#dbe3ea',
    background: '#f8fafc',
    badgeBackground: '#475569',
    badgeText: '#f8fafc',
    headerBackground: '#e7edf3',
    headerBorderColor: '#cbd5e1',
  },
};

const LIFECYCLE_SECTION_STYLES = {
  active: {
    borderColor: '#c7d8f7',
    background: '#f6f9ff',
    headerBackground: '#e8f0ff',
    headerBorderColor: '#bfd0f3',
  },
  scheduled: {
    borderColor: '#cfe3f8',
    background: '#f4fbff',
    headerBackground: '#e1f3ff',
    headerBorderColor: '#b9def7',
  },
  ended: {
    borderColor: '#e5d7dc',
    background: '#fcf8f9',
    headerBackground: '#f6ecef',
    headerBorderColor: '#e8d3da',
  },
} as const;

const BUNDLE_CARD_STYLE = {
  borderColor: '#c7d2fe',
  background: '#f8faff',
  badgeBackground: '#3730a3',
  badgeText: '#eef2ff',
};

const actionDockStyle = {
  position: 'fixed' as const,
  right: 24,
  bottom: 24,
  zIndex: 30,
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 14px',
  borderRadius: 20,
  background: 'rgba(255, 255, 255, 0.96)',
  border: '1px solid #d7dbe0',
  boxShadow: '0 16px 34px rgba(15, 23, 42, 0.16)',
  backdropFilter: 'blur(10px)',
};

const editButtonStyle = {
  backgroundColor: '#dbeafe',
  borderColor: '#60a5fa',
  color: '#1d4ed8',
  fontWeight: 700,
};

const deleteButtonStyle = {
  backgroundColor: '#fee2e2',
  borderColor: '#f87171',
  color: '#b91c1c',
  fontWeight: 700,
};

const toPascalCase = (value: string): string =>
  value
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');

const fromRewardFormToSavedReward = (reward: RewardFormInput): SavedRewardRow => {
  const type = reward.type as LevelRewardInputType;
  const base: SavedRewardRow = {
    __typename: toPascalCase(type),
    amount: reward.amount,
  };

  switch (type) {
    case LevelRewardInputType.CURRENCY_BOOST:
      return { ...base, boostType: reward.itemId };
    case LevelRewardInputType.CURRENCY_CATEGORY_FRAGMENT:
      return { ...base, category: reward.itemId ? { id: reward.itemId } : null };
    case LevelRewardInputType.CONTENT_PACK_REWARD:
      return { ...base, contentPack: reward.itemId ? { id: reward.itemId } : null };
    case LevelRewardInputType.STICKER:
    case LevelRewardInputType.PROFILE_FRAME:
    case LevelRewardInputType.APP_SKIN:
    case LevelRewardInputType.BADGE:
    case LevelRewardInputType.POSTER:
    case LevelRewardInputType.EMOJI:
    case LevelRewardInputType.EMOJI_PACK:
      return { ...base, id: reward.itemId };
    default:
      return reward.itemId ? { ...base, id: reward.itemId } : base;
  }
};

const toDraftCard = (card: LoyaltyCampaignCardRow): DraftLoyaltyCampaignCard => ({
  ...card,
  rewards: [...(card.rewards ?? [])],
  draftRewardInputs: (card.rewards ?? [])
    .map((reward) => toRewardFormInput(reward))
    .filter((reward): reward is RewardFormInput => reward !== null),
});

const isDraftCardId = (id: string): boolean => id.startsWith(DRAFT_CARD_ID_PREFIX);
const isDraftGroupId = (id: string): boolean => id.startsWith(DRAFT_GROUP_ID_PREFIX);

const getCardSectionId = (card: LoyaltyCampaignCardRow): LoyaltyCampaignCardSectionId => {
  if ((card.kind ?? 'BUNDLE') === 'BUNDLE') {
    return 'bundle';
  }

  switch (getItemCategoryFromReward(card.rewards?.[0])) {
    case 'COINS':
      return 'item-coins';
    case 'DIAMONDS':
      return 'item-diamonds';
    case 'BOOSTS':
      return 'item-boosts';
    case 'FRAGMENTS':
      return 'item-fragments';
    case 'VANITY':
      return 'item-vanity';
    default:
      return 'item-other';
  }
};

const getCardLifecycleState = (card: LoyaltyCampaignCardRow, now = moment.utc()): CardLifecycleState => {
  const startDate = card.startDate ? moment.utc(card.startDate) : null;
  const endDate = card.endDate ? moment.utc(card.endDate) : null;

  if (endDate && now.isSameOrAfter(endDate)) {
    return 'ended';
  }
  if (startDate && now.isBefore(startDate)) {
    return 'scheduled';
  }
  return 'active';
};

const serializeReward = (reward: SavedRewardRow): string =>
  JSON.stringify({
    type: reward.__typename ?? '',
    amount: reward.amount ?? 0,
    id: reward.id ?? '',
    boostType: reward.boostType ?? '',
    unlimitedBoostType: reward.unlimitedBoostType ?? '',
    categoryId: reward.category?.id ?? '',
    contentPackId: reward.contentPack?.id ?? '',
  });

const serializeConfig = (config: LoyaltyCampaignConfigRow): string => `${config.id}::${config.amount}`;

const serializeCard = (card: LoyaltyCampaignCardRow): string =>
  JSON.stringify({
    kind: card.kind ?? 'BUNDLE',
    name: card.name ?? '',
    groupId: card.groupId ?? null,
    groupName: card.groupName ?? null,
    groupDisplayOrder: card.groupDisplayOrder ?? null,
    itemDisplayOrder: card.itemDisplayOrder ?? null,
    cost: card.cost ?? 0,
    itemBonusLoyaltyPoints: card.itemBonusLoyaltyPoints ?? null,
    claimType: getCardClaimType(card.maxClaims, card.claimType),
    maxClaims: card.maxClaims ?? null,
    startDate: card.startDate ?? null,
    endDate: card.endDate ?? null,
    displayOrder: card.displayOrder ?? null,
    rewards: (card.rewards ?? []).map(serializeReward),
  });

const toMutationRewardInput = (
  reward: SavedRewardRow
): { type: string; amount: number; itemId: string | null } | null => {
  const rewardInput = toRewardFormInput(reward);
  if (!rewardInput) {
    return null;
  }

  return {
    type: rewardInput.type,
    amount: rewardInput.amount,
    itemId: rewardInput.itemId || null,
  };
};

const isDefined = <T,>(value: T | null): value is T => value !== null;

const sameRewardList = (left: SavedRewardRow[] = [], right: SavedRewardRow[] = []): boolean =>
  JSON.stringify(left.map(serializeReward)) === JSON.stringify(right.map(serializeReward));

const hasSameSerializedList = <T,>(left: T[], right: T[], serializer: (value: T) => string): boolean =>
  JSON.stringify(left.map(serializer)) === JSON.stringify(right.map(serializer));

type DraftLoyaltyCampaignCard = LoyaltyCampaignCardRow & {
  draftRewardInputs?: RewardFormInput[];
};

type LoyaltyCampaignItemGroup = {
  id: string;
  name: string;
  groupDisplayOrder: number;
  cards: LoyaltyCampaignCardRow[];
};

type EditableLoyaltyCampaignGroup = {
  originalGroupId: string;
  groupId: string;
  groupName: string;
  groupDisplayOrder: string;
  useSchedule: boolean;
  startDate: Date | null;
  endDate: Date | null;
};

type GroupedItemCardsByState = Record<CardLifecycleState, LoyaltyCampaignItemGroup[]>;

const replaceCardAt = (
  cards: DraftLoyaltyCampaignCard[],
  cardId: string,
  updater: (card: DraftLoyaltyCampaignCard) => DraftLoyaltyCampaignCard
): DraftLoyaltyCampaignCard[] => cards.map((card) => (card.id === cardId ? updater(card) : card));

const getClaimDisplayLabel = (maxClaims?: number | null, fallback: LoyaltyCampaignCardType = 'ONE_TIME'): string => {
  const claimType = getCardClaimType(maxClaims, fallback);
  if (claimType === 'INFINITE') {
    return 'UNLIMITED';
  }
  if ((maxClaims ?? 1) > 1) {
    return `${maxClaims} CLAIMS`;
  }
  return 'ONE TIME';
};

const buildItemGroups = (cards: LoyaltyCampaignCardRow[]): LoyaltyCampaignItemGroup[] =>
  Object.values(
    cards.reduce<Record<string, LoyaltyCampaignItemGroup>>((acc, card) => {
      const normalizedGroupId = (card.groupId ?? '').trim();
      const key = normalizedGroupId || `ungrouped:${card.id}`;
      if (!acc[key]) {
        acc[key] = {
          id: key,
          name: (card.groupName ?? '').trim() || (normalizedGroupId || 'Ungrouped Items'),
          groupDisplayOrder: card.groupDisplayOrder ?? Number.MAX_SAFE_INTEGER,
          cards: [],
        };
      }
      acc[key].cards.push(card);
      return acc;
    }, {})
  )
    .map((group) => ({
      ...group,
      cards: [...group.cards].sort((left, right) => {
        const leftOrder = left.itemDisplayOrder ?? left.displayOrder ?? 0;
        const rightOrder = right.itemDisplayOrder ?? right.displayOrder ?? 0;
        if (leftOrder !== rightOrder) {
          return leftOrder - rightOrder;
        }
        return (left.name ?? '').localeCompare(right.name ?? '');
      }),
    }))
    .sort((left, right) => {
      if (left.groupDisplayOrder !== right.groupDisplayOrder) {
        return left.groupDisplayOrder - right.groupDisplayOrder;
      }
      return left.name.localeCompare(right.name);
    });

const generateGroupId = (): string => {
  const cryptoApi = typeof globalThis !== 'undefined' ? (globalThis.crypto as Crypto & { randomUUID?: () => string }) : undefined;
  if (typeof cryptoApi?.randomUUID === 'function') {
    return cryptoApi.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const randomValue = Math.floor(Math.random() * 16);
    const value = char === 'x' ? randomValue : 8 + Math.floor(Math.random() * 4);
    return value.toString(16);
  });
};

const LoyaltyCampaign = () => {
  const [viewportWidth, setViewportWidth] = useState<number>(
    typeof window === 'undefined' ? 1280 : window.innerWidth
  );
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [configAmount, setConfigAmount] = useState('0');
  const [bulkSkusText, setBulkSkusText] = useState('');
  const [selectedSku, setSelectedSku] = useState('');
  const [configModalMode, setConfigModalMode] = useState<ConfigModalMode>('create');

  const [cardName, setCardName] = useState('');
  const [cardKind, setCardKind] = useState<LoyaltyCampaignCardKind>('BUNDLE');
  const [cardGroupId, setCardGroupId] = useState('');
  const [cardGroupName, setCardGroupName] = useState('');
  const [cardGroupDisplayOrder, setCardGroupDisplayOrder] = useState('');
  const [itemCardCategoryPreset, setItemCardCategoryPreset] = useState<LoyaltyCampaignItemCategory | null>(null);
  const [vanityItemTypePreset, setVanityItemTypePreset] = useState<VanityItemType | null>(null);
  const [cardCost, setCardCost] = useState('0');
  const [cardItemBaseStoreOfferId, setCardItemBaseStoreOfferId] = useState('');
  const [cardItemSKUPrice, setCardItemSKUPrice] = useState('');
  const [cardItemBonusLoyaltyPoints, setCardItemBonusLoyaltyPoints] = useState('');
  const [cardClaimType, setCardClaimType] = useState<LoyaltyCampaignClaimMode>('ONE_TIME');
  const [cardMaxClaims, setCardMaxClaims] = useState('3');
  const [cardUseSchedule, setCardUseSchedule] = useState(false);
  const [cardStartDate, setCardStartDate] = useState<Date | null>(moment.utc().startOf('day').toDate());
  const [cardEndDate, setCardEndDate] = useState<Date | null>(null);
  const [cardRewards, setCardRewards] = useState<RewardFormInput[]>([]);

  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showCardModal, setShowCardModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showQuickNavigation, setShowQuickNavigation] = useState(true);
  const [showBundleConfig, setShowBundleConfig] = useState(false);
  const [showGroupedItemConfig, setShowGroupedItemConfig] = useState(true);
  const [showSingleItemConfig, setShowSingleItemConfig] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [showEndedBundleCards, setShowEndedBundleCards] = useState(false);
  const [showEndedItemCards, setShowEndedItemCards] = useState(false);
  const [editingCardId, setEditingCardId] = useState<string | null>(null);
  const [showRewardModal, setShowRewardModal] = useState(false);
  const [selectedReward, setSelectedReward] = useState<LevelRewardInput | null>(null);
  const [editingRewardIndex, setEditingRewardIndex] = useState<number | null>(null);
  const [editingGroup, setEditingGroup] = useState<EditableLoyaltyCampaignGroup | null>(null);
  const [offerOptions, setOfferOptions] = useState<LoyaltyCampaignOfferOptionRow[]>([]);
  const [offerPriceMap, setOfferPriceMap] = useState<Record<string, number>>({});
  const [offerSkuMap, setOfferSkuMap] = useState<Record<string, string[]>>({});
  const [skuOptions, setSkuOptions] = useState<string[]>([]);
  const [skuPriceMap, setSkuPriceMap] = useState<Record<string, number>>({});
  const [configGroupAmount, setConfigGroupAmount] = useState<number | null>(null);
  const [editingGroupRows, setEditingGroupRows] = useState<LoyaltyCampaignConfigRow[]>([]);

  const [deletingConfigId, setDeletingConfigId] = useState<string | null>(null);
  const [deletingCardId, setDeletingCardId] = useState<string | null>(null);
  const [movingCardId, setMovingCardId] = useState<string | null>(null);
  const [autoImportLoading, setAutoImportLoading] = useState(false);
  const [savingConfigs, setSavingConfigs] = useState(false);
  const [savingCards, setSavingCards] = useState(false);
  const [savingAll, setSavingAll] = useState(false);
  const apolloClient = useApolloClient();
  const hasHydratedDraftRef = useRef(false);

  const { data, loading } = useQuery(GET_LOYALTY_CAMPAIGN_ADMIN, {
    fetchPolicy: 'no-cache',
  });

  const [upsertConfig] = useMutation(UPSERT_LOYALTY_CAMPAIGN_CONFIG);
  const [deleteConfig] = useMutation(DELETE_LOYALTY_CAMPAIGN_CONFIG);
  const [createCard] = useMutation(CREATE_LOYALTY_CAMPAIGN_CARD);
  const [updateCard] = useMutation(UPDATE_LOYALTY_CAMPAIGN_CARD);
  const [deleteCard] = useMutation(DELETE_LOYALTY_CAMPAIGN_CARD);

  const queryConfigs: LoyaltyCampaignConfigRow[] = useMemo(
    () => data?.liveOps?.loyaltyCampaignConfigs ?? [],
    [data?.liveOps?.loyaltyCampaignConfigs]
  );
  const queryCampaignCards: LoyaltyCampaignCardRow[] = useMemo(
    () => data?.liveOps?.loyaltyCampaignCards ?? [],
    [data?.liveOps?.loyaltyCampaignCards]
  );

  const [savedConfigs, setSavedConfigs] = useState<LoyaltyCampaignConfigRow[]>([]);
  const [draftConfigs, setDraftConfigs] = useState<LoyaltyCampaignConfigRow[]>([]);
  const [savedCards, setSavedCards] = useState<DraftLoyaltyCampaignCard[]>([]);
  const [draftCards, setDraftCards] = useState<DraftLoyaltyCampaignCard[]>([]);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (hasHydratedDraftRef.current || loading || !data) {
      return;
    }

    const nextConfigs = [...queryConfigs].sort((a, b) => a.id.localeCompare(b.id));
    const nextCards = [...queryCampaignCards]
      .sort((a, b) => {
        const left = a.displayOrder ?? 0;
        const right = b.displayOrder ?? 0;
        if (left !== right) {
          return left - right;
        }
        const leftCreated = a.created ? new Date(a.created).getTime() : 0;
        const rightCreated = b.created ? new Date(b.created).getTime() : 0;
        return leftCreated - rightCreated;
      })
      .map(toDraftCard);

    setSavedConfigs(nextConfigs);
    setDraftConfigs(nextConfigs);
    setSavedCards(nextCards);
    setDraftCards(nextCards);
    hasHydratedDraftRef.current = true;
  }, [data, loading, queryCampaignCards, queryConfigs]);

  useEffect(() => {
    let isMounted = true;
    apolloClient
      .query({
        query: GET_LOYALTY_CAMPAIGN_SKU_OPTIONS,
        fetchPolicy: 'no-cache',
      })
      .then((response) => {
        if (!isMounted) {
          return;
        }
        const nextOptions = response?.data?.liveOps?.loyaltyCampaignSkuOptions ?? [];
        setSkuOptions(Array.isArray(nextOptions) ? nextOptions : []);
        const nextPrices = response?.data?.liveOps?.loyaltyCampaignSkuPrices ?? [];
        setSkuPriceMap(buildSkuPriceMap(nextPrices as LoyaltyCampaignSkuPriceRow[]));
        const nextOffers = response?.data?.liveOps?.loyaltyCampaignOfferOptions ?? [];
        const parsedOffers = Array.isArray(nextOffers) ? (nextOffers as LoyaltyCampaignOfferOptionRow[]) : [];
        setOfferOptions(parsedOffers);
        setOfferPriceMap(buildOfferPriceMap(parsedOffers));
        setOfferSkuMap(buildOfferSkuMap(parsedOffers));
      })
      .catch(() => {
        if (isMounted) {
          setOfferOptions([]);
          setOfferPriceMap({});
          setOfferSkuMap({});
          setSkuOptions([]);
          setSkuPriceMap({});
        }
      });

    return () => {
      isMounted = false;
    };
  }, [apolloClient]);

  const pending = loading && !data;
  const uiScale = Math.max(0.82, Math.min(1, viewportWidth / 1440));
  const scaled = (value: number): number => Number((value * uiScale).toFixed(2));
  const offerById = useMemo(
    () =>
      offerOptions.reduce<Record<string, LoyaltyCampaignOfferOptionRow>>((acc, offer) => {
        const offerId = (offer.id ?? '').trim();
        if (offerId) {
          acc[offerId] = offer;
        }
        return acc;
      }, {}),
    [offerOptions]
  );
  const activeItemCategory =
    cardKind === 'ITEM'
      ? cardRewards[0]
        ? getItemCategoryFromRewardForm(cardRewards[0])
        : itemCardCategoryPreset
      : null;
  const activeVanityItemType =
    activeItemCategory === 'VANITY'
      ? cardRewards[0]
        ? getVanityItemTypeFromRewardForm(cardRewards[0])
        : vanityItemTypePreset
      : null;
  const activeItemRewardTypes =
    cardKind === 'ITEM' ? getAllowedRewardTypesForItemCategory(activeItemCategory, activeVanityItemType) : undefined;
  const directCurrencyRewardType =
    activeItemCategory === 'COINS'
      ? LevelRewardInputType.CURRENCY_COIN
      : activeItemCategory === 'DIAMONDS'
        ? LevelRewardInputType.CURRENCY_DIAMOND
        : null;
  const directCurrencyReward =
    directCurrencyRewardType && cardRewards[0]?.type === directCurrencyRewardType ? cardRewards[0] : null;
  const directCurrencyAmount = directCurrencyReward?.amount ?? 0;
  const normalizedCardCost = Number(cardCost);
  const hasLoyaltyPointCost = Number.isFinite(normalizedCardCost) && normalizedCardCost > 0;
  const hasSkuPrice = cardItemSKUPrice.trim().length > 0;
  const hasValidCardInput =
    (cardKind === 'ITEM' || cardName.trim().length > 0) &&
    Number.isFinite(normalizedCardCost) &&
    normalizedCardCost >= 0 &&
    (hasLoyaltyPointCost || hasSkuPrice) &&
    (cardClaimType !== 'CUSTOM' || (Number.isFinite(Number(cardMaxClaims)) && Number(cardMaxClaims) > 1)) &&
    (!cardUseSchedule ||
      (cardStartDate !== null && (cardEndDate === null || cardEndDate.getTime() >= cardStartDate.getTime())));
  const hasValidConfigAmount = Number.isFinite(Number(configAmount));

  useEffect(() => {
    if (cardKind !== 'ITEM' || (activeItemCategory !== 'COINS' && activeItemCategory !== 'DIAMONDS')) {
      return;
    }

    if (editingCardId) {
      return;
    }

    if (!directCurrencyAmount || !Number.isFinite(directCurrencyAmount)) {
      setCardName('');
      return;
    }

    setCardName(
      activeItemCategory === 'COINS'
        ? `${formatCompactNumber(directCurrencyAmount)} Coins`
        : `${formatCompactNumber(directCurrencyAmount)} Diamonds`
    );
  }, [activeItemCategory, cardKind, directCurrencyAmount, editingCardId]);

  useEffect(() => {
    if (cardKind !== 'ITEM' || activeItemCategory !== 'VANITY') {
      return;
    }

    if (editingCardId) {
      return;
    }

    const vanityReward = cardRewards[0];
    if (!vanityReward?.itemId) {
      return;
    }

    setCardName(toTitleLabel(vanityReward.itemId));
  }, [activeItemCategory, cardKind, cardRewards, editingCardId]);

  const configGroups = useMemo(() => {
    const groups = new Map<number, LoyaltyCampaignConfigRow[]>();
    for (const config of draftConfigs) {
      const amount = Number(config.amount ?? 0);
      const list = groups.get(amount) ?? [];
      list.push(config);
      groups.set(amount, list);
    }
    return Array.from(groups.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([amount, rows]) => ({ amount, rows: rows.sort((a, b) => a.id.localeCompare(b.id)) }));
  }, [draftConfigs]);

  const bundleCampaignCards = useMemo(
    () => draftCards.filter((card) => getCardSectionId(card) === 'bundle'),
    [draftCards]
  );

  const bundleCardGroupsByState = useMemo(() => {
    const now = moment.utc();
    return {
      scheduled: bundleCampaignCards.filter((card) => getCardLifecycleState(card, now) === 'scheduled'),
      active: bundleCampaignCards.filter((card) => getCardLifecycleState(card, now) === 'active'),
      ended: bundleCampaignCards.filter((card) => getCardLifecycleState(card, now) === 'ended'),
    };
  }, [bundleCampaignCards]);

  const getCardPricingLabel = (card: LoyaltyCampaignCardRow): string => `${card.cost} LP`;

  const serializeComparableCard = (card: LoyaltyCampaignCardRow): string =>
    JSON.stringify(JSON.parse(serializeCard(card)));

  const hasSameCards = (left: LoyaltyCampaignCardRow[], right: LoyaltyCampaignCardRow[]): boolean =>
    hasSameSerializedList(left, right, serializeComparableCard);

  const countChangedRows = <T extends { id: string }>(
    draftRows: T[],
    savedRows: T[],
    serializer: (value: T) => string
  ): number =>
    draftRows.filter((draftRow) => {
      const savedRow = savedRows.find((row) => row.id === draftRow.id);
      return !savedRow || serializer(savedRow) !== serializer(draftRow);
    }).length + savedRows.filter((savedRow) => !draftRows.some((draftRow) => draftRow.id === savedRow.id)).length;

  const itemCardGroupsByState = useMemo(() => {
    const createEmptyGroups = (): Record<LoyaltyCampaignItemCategory, LoyaltyCampaignCardRow[]> => ({
      COINS: [],
      DIAMONDS: [],
      BOOSTS: [],
      FRAGMENTS: [],
      VANITY: [],
      OTHER: [],
    });
    const now = moment.utc();
    const groups: Record<CardLifecycleState, Record<LoyaltyCampaignItemCategory, LoyaltyCampaignCardRow[]>> = {
      scheduled: createEmptyGroups(),
      active: createEmptyGroups(),
      ended: createEmptyGroups(),
    };

    for (const card of draftCards.filter((currentCard) => getCardSectionId(currentCard) !== 'bundle')) {
      const lifecycleState = getCardLifecycleState(card, now);
      groups[lifecycleState][getItemCategoryFromReward(card.rewards?.[0])].push(card);
    }

    return groups;
  }, [draftCards]);
  const activeItemCardGroups = itemCardGroupsByState.active;
  const scheduledItemCardGroups = itemCardGroupsByState.scheduled;
  const endedItemCardGroups = itemCardGroupsByState.ended;

  const groupedItemCardsByState = useMemo((): GroupedItemCardsByState => {
    const buildGroups = (cardsByCategory: Record<LoyaltyCampaignItemCategory, LoyaltyCampaignCardRow[]>) =>
      buildItemGroups(
        Object.values(cardsByCategory)
          .flat()
          .filter((card) => (card.groupId ?? '').trim().length > 0)
      );

    return {
      active: buildGroups(activeItemCardGroups),
      scheduled: buildGroups(scheduledItemCardGroups),
      ended: buildGroups(endedItemCardGroups),
    };
  }, [activeItemCardGroups, scheduledItemCardGroups, endedItemCardGroups]);

  const ungroupedItemCardsByState = useMemo(() => {
    const filterUngrouped = (cardsByCategory: Record<LoyaltyCampaignItemCategory, LoyaltyCampaignCardRow[]>) =>
      (Object.keys(cardsByCategory) as LoyaltyCampaignItemCategory[]).reduce<
        Record<LoyaltyCampaignItemCategory, LoyaltyCampaignCardRow[]>
      >(
        (acc, category) => {
          acc[category] = cardsByCategory[category].filter((card) => (card.groupId ?? '').trim().length === 0);
          return acc;
        },
        {
          COINS: [],
          DIAMONDS: [],
          BOOSTS: [],
          FRAGMENTS: [],
          VANITY: [],
          OTHER: [],
        }
      );

    return {
      active: filterUngrouped(activeItemCardGroups),
      scheduled: filterUngrouped(scheduledItemCardGroups),
      ended: filterUngrouped(endedItemCardGroups),
    };
  }, [activeItemCardGroups, scheduledItemCardGroups, endedItemCardGroups]);

  const isConfigDirty = useMemo(() => {
    const saved = [...savedConfigs].sort((a, b) => a.id.localeCompare(b.id));
    const draft = [...draftConfigs].sort((a, b) => a.id.localeCompare(b.id));
    return !hasSameSerializedList(saved, draft, serializeConfig);
  }, [draftConfigs, savedConfigs]);

  const isCardSectionDirty = (sectionId: LoyaltyCampaignCardSectionId): boolean => {
    const saved = savedCards.filter((card) => getCardSectionId(card) === sectionId);
    const draft = draftCards.filter((card) => getCardSectionId(card) === sectionId);
    return !hasSameCards(saved, draft);
  };

  const isAnyCardDirty = CARD_SECTION_IDS.some((sectionId) => isCardSectionDirty(sectionId));
  const isGroupedItemDirty = !hasSameCards(
    savedCards.filter((card) => (card.kind ?? 'BUNDLE') === 'ITEM' && (card.groupId ?? '').trim().length > 0),
    draftCards.filter((card) => (card.kind ?? 'BUNDLE') === 'ITEM' && (card.groupId ?? '').trim().length > 0)
  );
  const isSingleItemDirty = !hasSameCards(
    savedCards.filter((card) => (card.kind ?? 'BUNDLE') === 'ITEM' && (card.groupId ?? '').trim().length === 0),
    draftCards.filter((card) => (card.kind ?? 'BUNDLE') === 'ITEM' && (card.groupId ?? '').trim().length === 0)
  );

  const hasAnyDraftChanges = isConfigDirty || isAnyCardDirty;
  const changedConfigCount = countChangedRows(draftConfigs, savedConfigs, serializeConfig);
  const changedCardCount = countChangedRows(draftCards, savedCards, serializeComparableCard);

  const buildRewardMutationPayload = (
    card: DraftLoyaltyCampaignCard
  ): Array<{ type: string; amount: number; itemId: string | null }> =>
    (card.draftRewardInputs ?? [])
      .map((reward) => ({
        type: reward.type,
        amount: reward.amount,
        itemId: reward.itemId || null,
      }))
      .filter(isDefined);

  const buildCardUpdateVariables = (
    savedCard: DraftLoyaltyCampaignCard | undefined,
    nextCard: DraftLoyaltyCampaignCard
  ): Record<string, unknown> => {
    const variables: Record<string, unknown> = {
      id: nextCard.id,
    };

    if (!savedCard || (savedCard.kind ?? 'BUNDLE') !== (nextCard.kind ?? 'BUNDLE')) {
      variables.kind = nextCard.kind ?? 'BUNDLE';
    }
    if (!savedCard || (savedCard.name ?? '') !== (nextCard.name ?? '')) {
      variables.name = nextCard.name.trim();
    }
    if (!savedCard || (savedCard.groupId ?? null) !== (nextCard.groupId ?? null)) {
      variables.groupId = nextCard.groupId ?? null;
    }
    if (!savedCard || (savedCard.groupName ?? null) !== (nextCard.groupName ?? null)) {
      variables.groupName = nextCard.groupName ?? null;
    }
    if (!savedCard || (savedCard.groupDisplayOrder ?? null) !== (nextCard.groupDisplayOrder ?? null)) {
      variables.groupDisplayOrder = nextCard.groupDisplayOrder ?? null;
    }
    if (!savedCard || (savedCard.itemDisplayOrder ?? null) !== (nextCard.itemDisplayOrder ?? null)) {
      variables.itemDisplayOrder = nextCard.itemDisplayOrder ?? null;
    }
    if (!savedCard || Number(savedCard.cost ?? 0) !== Number(nextCard.cost ?? 0)) {
      variables.cost = Number(nextCard.cost);
    }
    if (
      !savedCard ||
      Number(savedCard.itemBonusLoyaltyPoints ?? 0) !== Number(nextCard.itemBonusLoyaltyPoints ?? 0)
    ) {
      variables.itemBonusLoyaltyPoints = nextCard.itemBonusLoyaltyPoints ?? null;
    }
    if (!savedCard || (savedCard.displayOrder ?? 0) !== (nextCard.displayOrder ?? 0)) {
      variables.displayOrder = nextCard.displayOrder ?? 0;
    }
    if (!savedCard || (savedCard.maxClaims ?? 1) !== (nextCard.maxClaims ?? 1)) {
      variables.maxClaims = nextCard.maxClaims ?? 1;
    }
    if (!savedCard || (savedCard.startDate ?? null) !== (nextCard.startDate ?? null)) {
      variables.startDate = nextCard.startDate ?? null;
    }
    if (!savedCard || (savedCard.endDate ?? null) !== (nextCard.endDate ?? null)) {
      variables.endDate = nextCard.endDate ?? null;
    }
    if (!savedCard || !sameRewardList(savedCard.rewards, nextCard.rewards)) {
      variables.rewards = buildRewardMutationPayload(nextCard);
    }

    return variables;
  };

  const clearConfigForm = () => {
    setConfigAmount('0');
    setBulkSkusText('');
    setSelectedSku('');
    setConfigModalMode('create');
    setConfigGroupAmount(null);
    setEditingGroupRows([]);
  };

  const openCreateConfigModal = () => {
    clearConfigForm();
    setConfigModalMode('create');
    setConfigGroupAmount(null);
    setShowConfigModal(true);
    setError(null);
  };

  const onSaveConfigDraft = () => {
    setError(null);
    setNotice(null);
    const amount = Number(configAmount);
    if (!Number.isFinite(amount)) {
      setError('Invalid points amount.');
      return;
    }

    if (configModalMode === 'groupEdit') {
      if (editingGroupRows.length === 0) {
        setError('No SKU group selected.');
        return;
      }

      const editingIds = new Set(editingGroupRows.map((row) => row.id));
      setDraftConfigs((prev) =>
        prev.map((row) => (editingIds.has(row.id) ? { ...row, amount } : row)).sort((a, b) => a.id.localeCompare(b.id))
      );
      setShowConfigModal(false);
      clearConfigForm();
      setNotice('Config group updated in draft. Click Save in section or Save All to persist.');
      return;
    }

    const ids = parseSkus(bulkSkusText);
    if (ids.length === 0) {
      setError('Add at least one SKU.');
      return;
    }

    setDraftConfigs((prev) => {
      const map = new Map(prev.map((row) => [row.id, row]));
      for (const id of ids) {
        map.set(id, { id, amount });
      }
      return Array.from(map.values()).sort((a, b) => a.id.localeCompare(b.id));
    });
    setShowConfigModal(false);
    clearConfigForm();
    setNotice('Config changes staged locally. Click Save in section or Save All to persist.');
  };

  const onDeleteConfigDraft = (id: string) => {
    const shouldDelete = window.confirm(`Delete SKU config "${id}" from draft?`);
    if (!shouldDelete) {
      return;
    }

    setError(null);
    setNotice(null);
    setDeletingConfigId(id);
    setDraftConfigs((prev) => prev.filter((row) => row.id !== id));
    setDeletingConfigId(null);
    setNotice('Config removed from draft. Click Save in section or Save All to persist.');
  };

  const onEditGroupAmount = (rows: LoyaltyCampaignConfigRow[]) => {
    if (rows.length === 0) {
      return;
    }
    clearConfigForm();
    setConfigModalMode('groupEdit');
    setEditingGroupRows(rows);
    setConfigAmount(String(rows[0]?.amount ?? 0));
    setBulkSkusText(rows.map((row) => row.id).join('\n'));
    setShowConfigModal(true);
    setError(null);
    setNotice(null);
  };

  const onDeleteGroupDraft = (rows: LoyaltyCampaignConfigRow[]) => {
    if (rows.length === 0) {
      return;
    }
    const shouldDelete = window.confirm(`Delete this group and remove ${rows.length} SKU config(s) from draft?`);
    if (!shouldDelete) {
      return;
    }

    const rowIds = new Set(rows.map((row) => row.id));
    setError(null);
    setNotice(null);
    setDeletingConfigId(`group:${rows[0].amount}`);
    setDraftConfigs((prev) => prev.filter((row) => !rowIds.has(row.id)));
    setDeletingConfigId(null);
    setNotice('Config group removed from draft. Click Save in section or Save All to persist.');
  };

  const onAddSkuToGroup = (amount: number) => {
    clearConfigForm();
    setConfigModalMode('groupAdd');
    setConfigAmount(String(amount));
    setConfigGroupAmount(amount);
    setShowConfigModal(true);
    setError(null);
    setNotice(null);
  };

  const clearCardForm = () => {
    setEditingCardId(null);
    setCardName('');
    setCardKind('BUNDLE');
    setCardGroupId('');
    setCardGroupName('');
    setCardGroupDisplayOrder('');
    setItemCardCategoryPreset(null);
    setVanityItemTypePreset(null);
    setCardCost('0');
    setCardItemBaseStoreOfferId('');
    setCardItemSKUPrice('');
    setCardItemBonusLoyaltyPoints('');
    setCardClaimType('ONE_TIME');
    setCardMaxClaims('3');
    setCardUseSchedule(false);
    setCardStartDate(moment.utc().startOf('day').toDate());
    setCardEndDate(null);
    setCardRewards([]);
    setShowRewardModal(false);
    setSelectedReward(null);
    setEditingRewardIndex(null);
    setError(null);
  };

  const clearGroupForm = () => {
    setEditingGroup(null);
    setShowGroupModal(false);
  };

  const openEditGroupModal = (group: LoyaltyCampaignItemGroup) => {
    if (group.id.startsWith('ungrouped:') || group.cards.length === 0) {
      return;
    }
    const firstCard = group.cards[0];
    setEditingGroup({
      originalGroupId: group.id,
      groupId: group.id,
      groupName: group.name,
      groupDisplayOrder:
        firstCard.groupDisplayOrder === null || firstCard.groupDisplayOrder === undefined
          ? ''
          : String(firstCard.groupDisplayOrder),
      useSchedule: Boolean(firstCard.startDate || firstCard.endDate),
      startDate: firstCard.startDate ? new Date(firstCard.startDate) : moment.utc().startOf('day').toDate(),
      endDate: firstCard.endDate ? new Date(firstCard.endDate) : null,
    });
    setShowGroupModal(true);
    setError(null);
    setNotice(null);
  };

  const saveGroupDraft = () => {
    if (!editingGroup) {
      return;
    }
    if (editingGroup.groupId.trim().length === 0) {
      setError('Group ID is required.');
      return;
    }
    if (editingGroup.groupName.trim().length === 0) {
      setError('Group Name is required.');
      return;
    }
    if (editingGroup.useSchedule && !editingGroup.startDate) {
      setError('Group start date is required.');
      return;
    }
    if (
      editingGroup.useSchedule &&
      editingGroup.startDate &&
      editingGroup.endDate &&
      editingGroup.endDate.getTime() < editingGroup.startDate.getTime()
    ) {
      setError('Group end date must be later than or equal to start date.');
      return;
    }

    const normalizedGroupId = editingGroup.groupId.trim();
    const normalizedGroupName = editingGroup.groupName.trim();
    const normalizedGroupDisplayOrder =
      editingGroup.groupDisplayOrder.trim() === '' ? null : Number(editingGroup.groupDisplayOrder);
    const normalizedStartDate =
      editingGroup.useSchedule && editingGroup.startDate ? editingGroup.startDate.toISOString() : null;
    const normalizedEndDate =
      editingGroup.useSchedule && editingGroup.endDate ? editingGroup.endDate.toISOString() : null;
    const shouldCreateFirstItem = isDraftGroupId(editingGroup.originalGroupId);
    const existingGroupCards = draftCards.filter(
      (card) => card.kind === 'ITEM' && (card.groupId ?? '').trim() === normalizedGroupId
    );
    if (shouldCreateFirstItem && existingGroupCards.length === 0) {
      setError('Add at least one item before creating the group.');
      return;
    }
    const hasGroupIdConflict = draftCards.some(
      (card) =>
        card.kind === 'ITEM' &&
        (card.groupId ?? '').trim() === normalizedGroupId &&
        (card.groupId ?? '').trim() !== editingGroup.originalGroupId
    );
    if (hasGroupIdConflict) {
      setError('Group ID already exists. Use a different group or add items to the existing one.');
      return;
    }

    setDraftCards((prev) =>
      prev.map((card) =>
        card.kind === 'ITEM' && (card.groupId ?? '').trim() === editingGroup.originalGroupId
          ? {
              ...card,
              groupId: normalizedGroupId,
              groupName: normalizedGroupName,
              groupDisplayOrder: normalizedGroupDisplayOrder,
              startDate: normalizedStartDate,
              endDate: normalizedEndDate,
            }
          : card
      )
    );
    setNotice(
      shouldCreateFirstItem
        ? 'Group added. Click Save in section or Save All to persist.'
        : 'Group changes saved locally. Click Save in section or Save All to persist.'
    );
    clearGroupForm();
  };

  const deleteGroupDraft = (group: LoyaltyCampaignItemGroup) => {
    if (group.id.startsWith('ungrouped:')) {
      return;
    }
    const shouldDelete = window.confirm(`Delete group "${group.name}" and all items inside it from draft?`);
    if (!shouldDelete) {
      return;
    }
    setDraftCards((prev) =>
      prev.filter((card) => !(card.kind === 'ITEM' && (card.groupId ?? '').trim() === group.id))
    );
    setNotice('Group removed from draft. Click Save in section or Save All to persist.');
  };

  const openCreateCardModal = (
    kind: LoyaltyCampaignCardKind,
    itemCategory?: LoyaltyCampaignItemCategory,
    vanityItemType?: VanityItemType,
    groupPreset?: {
      groupId: string;
      groupName: string;
      groupDisplayOrder?: number | null;
      useSchedule?: boolean;
      startDate?: Date | null;
      endDate?: Date | null;
    }
  ) => {
    clearCardForm();
    setCardKind(kind);
    setCardGroupId(groupPreset?.groupId ?? '');
    setCardGroupName(groupPreset?.groupName ?? '');
    setCardGroupDisplayOrder(
      groupPreset?.groupDisplayOrder === null || groupPreset?.groupDisplayOrder === undefined
        ? ''
        : String(groupPreset.groupDisplayOrder)
    );
    setItemCardCategoryPreset(kind === 'ITEM' ? itemCategory ?? null : null);
    setVanityItemTypePreset(kind === 'ITEM' && itemCategory === 'VANITY' ? vanityItemType ?? null : null);
    if (kind === 'ITEM' && groupPreset) {
      setCardUseSchedule(Boolean(groupPreset.useSchedule));
      setCardStartDate(groupPreset.startDate ?? moment.utc().startOf('day').toDate());
      setCardEndDate(groupPreset.endDate ?? null);
    }
    setShowCardModal(true);
  };

  const openCreateGroupModal = () => {
    const nextGroupNumber =
      buildItemGroups(draftCards.filter((card) => card.kind === 'ITEM' && (card.groupId ?? '').trim().length > 0)).length + 1;
    const generatedGroupId = generateGroupId();
    setEditingGroup({
      originalGroupId: `${DRAFT_GROUP_ID_PREFIX}${Date.now()}`,
      groupId: generatedGroupId,
      groupName: `Item Group ${nextGroupNumber}`,
      groupDisplayOrder: String(
        groupedItemCardsByState.active.length +
          groupedItemCardsByState.scheduled.length +
          groupedItemCardsByState.ended.length
      ),
      useSchedule: false,
      startDate: moment.utc().startOf('day').toDate(),
      endDate: null,
    });
    setShowGroupModal(true);
    setError(null);
    setNotice(null);
  };

  const onChangeDirectCurrencyAmount = (rawAmount: string) => {
    if (!directCurrencyRewardType) {
      return;
    }

    if (!rawAmount.trim()) {
      setCardRewards([]);
      return;
    }

    const amount = Number(rawAmount);
    setCardRewards([
      {
        type: directCurrencyRewardType,
        amount: Number.isFinite(amount) ? amount : 0,
        itemId: '',
      },
    ]);
  };

  const onSaveRewardModal = (newReward: LevelRewardInput) => {
    const nextReward = fromLevelRewardInputToRewardForm(newReward);
    if (editingRewardIndex !== null) {
      setCardRewards((prev) => prev.map((r, idx) => (idx === editingRewardIndex ? nextReward : r)));
    } else {
      setCardRewards((prev) => [...prev, nextReward]);
    }
    setShowRewardModal(false);
    setSelectedReward(null);
    setEditingRewardIndex(null);
    setError(null);
  };

  const onOpenAddRewardModal = () => {
    if (cardKind === 'ITEM' && cardRewards.length >= 1) {
      setError('Campaign card only supports one item. Remove the current item before adding another.');
      return;
    }
    setEditingRewardIndex(null);
    setSelectedReward(null);
    setShowRewardModal(true);
  };

  const onOpenEditRewardModal = (index: number) => {
    const reward = cardRewards[index];
    if (!reward) {
      return;
    }
    setSelectedReward(fromRewardFormToLevelRewardInput(reward));
    setEditingRewardIndex(index);
    setShowRewardModal(true);
  };

  const onSaveCardDraft = () => {
    setError(null);
    setNotice(null);
    if (cardUseSchedule && !cardStartDate) {
      setError('Start date is required.');
      return;
    }
    if (cardUseSchedule && cardStartDate && cardEndDate && cardEndDate.getTime() < cardStartDate.getTime()) {
      setError('End date must be later than or equal to start date.');
      return;
    }
    if (cardRewards.length === 0) {
      setError(cardKind === 'ITEM' ? 'Add one item first.' : 'Add at least one reward first.');
      return;
    }
    if (cardKind === 'ITEM' && cardRewards.length > 1) {
      setError('Campaign card only supports one item. Bundles are not allowed.');
      return;
    }
    if (cardKind === 'ITEM' && cardGroupId.trim().length === 0) {
      setError('Group ID is required for item cards.');
      return;
    }
    if (cardKind === 'ITEM' && cardGroupName.trim().length === 0) {
      setError('Group Name is required for item cards.');
      return;
    }
    if (cardClaimType === 'CUSTOM' && (!Number.isFinite(Number(cardMaxClaims)) || Number(cardMaxClaims) <= 1)) {
      setError('Custom claim value must be greater than 1.');
      return;
    }
    if (!Number.isFinite(Number(cardCost)) || Number(cardCost) < 0) {
      setError('Loyalty Point Cost must be 0 or higher.');
      return;
    }
    if (Number(cardCost) > 0 && cardItemSKUPrice.trim().length > 0) {
      setError('Choose only one pricing mode: Loyalty Point Cost or Store Offer / SKU.');
      return;
    }
    if (Number(cardCost) <= 0 && cardItemSKUPrice.trim().length === 0) {
      setError('Set Loyalty Point Cost or Store Offer / SKU.');
      return;
    }
    if (cardItemBaseStoreOfferId.trim().length > 0 && cardItemSKUPrice.trim().length === 0) {
      setError('Set Active Store Offer before setting Base Store Offer.');
      return;
    }
    if (
      cardItemBaseStoreOfferId.trim().length > 0 &&
      cardItemBaseStoreOfferId.trim() === cardItemSKUPrice.trim()
    ) {
      setError('Base Store Offer must be different from Active Store Offer.');
      return;
    }
    const shouldCloseCreateGroupModal =
      cardKind === 'ITEM' &&
      !!editingGroup &&
      isDraftGroupId(editingGroup.originalGroupId) &&
      cardGroupId.trim() === editingGroup.groupId.trim();

    const normalizedMaxClaims =
      cardClaimType === 'INFINITE' ? 0 : cardClaimType === 'CUSTOM' ? Math.max(2, Math.floor(Number(cardMaxClaims))) : 1;

    const existingCard = editingCardId ? draftCards.find((card) => card.id === editingCardId) : undefined;
    const normalizedGroupId = cardKind === 'ITEM' ? cardGroupId.trim() : '';
    const normalizedGroupName = cardKind === 'ITEM' ? cardGroupName.trim() : '';
    const derivedGroupDisplayOrder =
      cardKind === 'ITEM'
        ? existingCard?.groupDisplayOrder ??
          (cardGroupDisplayOrder.trim() !== ''
            ? Number(cardGroupDisplayOrder)
            : draftCards.find((card) => (card.groupId ?? '').trim() === normalizedGroupId)?.groupDisplayOrder ??
              groupedItemCardsByState.active.length +
                groupedItemCardsByState.scheduled.length +
                groupedItemCardsByState.ended.length)
        : null;
    const derivedItemDisplayOrder =
      cardKind === 'ITEM'
        ? existingCard?.itemDisplayOrder ??
          draftCards.filter((card) => (card.groupId ?? '').trim() === normalizedGroupId).length
        : null;
    const nextCard: DraftLoyaltyCampaignCard = {
      id: editingCardId ?? `${DRAFT_CARD_ID_PREFIX}${Date.now()}`,
      kind: cardKind,
      displayOrder: existingCard?.displayOrder,
      name: cardName.trim(),
      groupId: cardKind === 'ITEM' ? normalizedGroupId || null : null,
      groupName: cardKind === 'ITEM' ? normalizedGroupName || null : null,
      groupDisplayOrder: derivedGroupDisplayOrder,
      itemDisplayOrder: derivedItemDisplayOrder,
      cost: Number(cardCost),
      itemBonusLoyaltyPoints: cardItemBonusLoyaltyPoints.trim() ? Number(cardItemBonusLoyaltyPoints) : null,
      claimType: normalizedMaxClaims === 0 ? 'INFINITE' : 'ONE_TIME',
      maxClaims: normalizedMaxClaims,
      startDate: cardUseSchedule && cardStartDate ? cardStartDate.toISOString() : null,
      endDate: cardUseSchedule && cardEndDate ? cardEndDate.toISOString() : null,
      rewards: cardRewards.map(fromRewardFormToSavedReward),
      draftRewardInputs: [...cardRewards],
    };

    if (editingCardId) {
      if (existingCard && serializeComparableCard(existingCard) === serializeComparableCard(nextCard)) {
        setShowCardModal(false);
        clearCardForm();
        setNotice('No card changes detected.');
        return;
      }
    }

    setDraftCards((prev) => {
      if (editingCardId) {
        return replaceCardAt(prev, editingCardId, () => nextCard);
      }
      return [...prev, nextCard];
    });

    setShowCardModal(false);
    clearCardForm();
    if (shouldCloseCreateGroupModal) {
      clearGroupForm();
      setNotice('Group added. Click Save in section or Save All to persist.');
      return;
    }

    setNotice('Card changes staged locally. Click Save in section or Save All to persist.');
  };

  const onEditCard = (card: DraftLoyaltyCampaignCard) => {
    setEditingCardId(card.id);
    setCardName(card.name ?? '');
    setCardKind(card.kind ?? 'BUNDLE');
    setCardGroupId(card.groupId ?? '');
    setCardGroupName(card.groupName ?? '');
    setCardGroupDisplayOrder(
      card.groupDisplayOrder === null || card.groupDisplayOrder === undefined ? '' : String(card.groupDisplayOrder)
    );
    setItemCardCategoryPreset((card.kind ?? 'BUNDLE') === 'ITEM' ? getItemCategoryFromReward(card.rewards?.[0]) : null);
    setVanityItemTypePreset((card.kind ?? 'BUNDLE') === 'ITEM' ? getVanityItemTypeFromReward(card.rewards?.[0]) : null);
    setCardCost(String(card.cost ?? 0));
    setCardItemBaseStoreOfferId('');
    setCardItemSKUPrice('');
    setCardItemBonusLoyaltyPoints(card.itemBonusLoyaltyPoints ? String(card.itemBonusLoyaltyPoints) : '');
    setCardClaimType(getCardClaimMode(card.maxClaims));
    setCardMaxClaims(String(card.maxClaims && card.maxClaims > 1 ? card.maxClaims : 3));
    setCardUseSchedule(Boolean(card.startDate || card.endDate));
    setCardStartDate(card.startDate ? new Date(card.startDate) : moment.utc().startOf('day').toDate());
    setCardEndDate(card.endDate ? new Date(card.endDate) : null);
    setCardRewards(card.draftRewardInputs ?? []);
    setError(null);
    setNotice(null);
    setShowCardModal(true);
  };

  const onDeleteCardDraft = (id: string) => {
    const shouldDelete = window.confirm('Delete this card from draft?');
    if (!shouldDelete) {
      return;
    }

    setError(null);
    setNotice(null);
    setDeletingCardId(id);
    setDraftCards((prev) => prev.filter((card) => card.id !== id));
    if (editingCardId === id) {
      clearCardForm();
      setShowCardModal(false);
    }
    setDeletingCardId(null);
    setNotice('Card removed from draft. Click Save in section or Save All to persist.');
  };

  const onMoveCardDraft = (cardId: string, direction: 'up' | 'down', sectionId: LoyaltyCampaignCardSectionId) => {
    setError(null);
    setNotice(null);
    setMovingCardId(cardId);
    setDraftCards((prev) => {
      const sectionIndexes = prev.reduce<number[]>((list, card, index) => {
        if (getCardSectionId(card) === sectionId) {
          list.push(index);
        }
        return list;
      }, []);
      const currentSectionIndex = sectionIndexes.findIndex((index) => prev[index]?.id === cardId);
      const targetSectionIndex = direction === 'up' ? currentSectionIndex - 1 : currentSectionIndex + 1;
      if (currentSectionIndex === -1 || targetSectionIndex < 0 || targetSectionIndex >= sectionIndexes.length) {
        return prev;
      }

      const currentIndex = sectionIndexes[currentSectionIndex];
      const targetIndex = sectionIndexes[targetSectionIndex];
      const next = [...prev];
      const currentCard = next[currentIndex];
      const targetCard = next[targetIndex];
      if (!currentCard || !targetCard) {
        return prev;
      }
      next[currentIndex] = targetCard;
      next[targetIndex] = currentCard;
      return next;
    });
    setMovingCardId(null);
    setNotice('Card order updated in draft. Click Save in section or Save All to persist.');
  };

  const onMoveGroupDraft = (groupId: string, direction: 'up' | 'down') => {
    setError(null);
    setNotice(null);
    setMovingCardId(`group:${groupId}`);
    setDraftCards((prev) => {
      const groupedCards = prev.filter(
        (card) => (card.kind ?? 'BUNDLE') === 'ITEM' && (card.groupId ?? '').trim().length > 0
      );
      const groups = buildItemGroups(groupedCards);
      const currentIndex = groups.findIndex((group) => group.id === groupId);
      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (currentIndex === -1 || targetIndex < 0 || targetIndex >= groups.length) {
        return prev;
      }

      const reorderedGroups = [...groups];
      const [movedGroup] = reorderedGroups.splice(currentIndex, 1);
      reorderedGroups.splice(targetIndex, 0, movedGroup);
      const groupOrderMap = reorderedGroups.reduce<Record<string, number>>((acc, group, index) => {
        acc[group.id] = index;
        return acc;
      }, {});

      return prev.map((card) => {
        const normalizedGroupId = (card.groupId ?? '').trim();
        if ((card.kind ?? 'BUNDLE') !== 'ITEM' || !normalizedGroupId || groupOrderMap[normalizedGroupId] === undefined) {
          return card;
        }
        return {
          ...card,
          groupDisplayOrder: groupOrderMap[normalizedGroupId],
        };
      });
    });
    setMovingCardId(null);
    setNotice('Group order updated in draft. Click Save in section or Save All to persist.');
  };

  const onMoveGroupItemDraft = (groupId: string, cardId: string, direction: 'up' | 'down') => {
    setError(null);
    setNotice(null);
    setMovingCardId(cardId);
    setDraftCards((prev) => {
      const groupedCards = buildItemGroups(
        prev.filter((card) => (card.kind ?? 'BUNDLE') === 'ITEM' && (card.groupId ?? '').trim() === groupId)
      )[0]?.cards;
      if (!groupedCards || groupedCards.length === 0) {
        return prev;
      }

      const currentIndex = groupedCards.findIndex((card) => card.id === cardId);
      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (currentIndex === -1 || targetIndex < 0 || targetIndex >= groupedCards.length) {
        return prev;
      }

      const reorderedCards = [...groupedCards];
      const [movedCard] = reorderedCards.splice(currentIndex, 1);
      reorderedCards.splice(targetIndex, 0, movedCard);
      const itemOrderMap = reorderedCards.reduce<Record<string, number>>((acc, card, index) => {
        acc[card.id] = index;
        return acc;
      }, {});

      return prev.map((card) =>
        itemOrderMap[card.id] === undefined
          ? card
          : {
              ...card,
              itemDisplayOrder: itemOrderMap[card.id],
            }
      );
    });
    setMovingCardId(null);
    setNotice('Item order updated in draft. Click Save in section or Save All to persist.');
  };

  const onAutoImportConfigByPrice = () => {
    setError(null);
    setNotice(null);

    const confirmed = window.confirm(
      'Auto import will stage all SKUs that have a price-to-loyalty-points mapping. Continue?'
    );
    if (!confirmed) {
      return;
    }

    const allSkus = Array.from(new Set([...skuOptions, ...draftConfigs.map((config) => config.id)]));
    if (allSkus.length === 0) {
      setError('No SKU available for auto import.');
      return;
    }

    const operations: Array<{ id: string; amount: number }> = [];
    const skippedNoPrice: string[] = [];
    const skippedNoRule: string[] = [];
    const skippedNoRuleDetails: Array<{ sku: string; priceUsd: number }> = [];

    for (const sku of allSkus) {
      const price = getSkuPriceUSD(sku, skuPriceMap);
      if (price === null) {
        skippedNoPrice.push(sku);
      } else {
        const points = getLoyaltyPointsByPrice(price);
        if (points === null) {
          skippedNoRule.push(sku);
          skippedNoRuleDetails.push({ sku, priceUsd: price });
        } else {
          operations.push({ id: sku, amount: points });
        }
      }
    }

    if (operations.length === 0) {
      setError(
        `No SKU matched price rule. Missing price: ${skippedNoPrice.length}, unsupported price: ${skippedNoRule.length}.`
      );
      return;
    }

    setAutoImportLoading(true);
    setDraftConfigs((prev) => {
      const map = new Map(prev.map((row) => [row.id, row]));
      for (const operation of operations) {
        map.set(operation.id, operation);
      }
      return Array.from(map.values()).sort((a, b) => a.id.localeCompare(b.id));
    });
    const unsupportedList = skippedNoRuleDetails
      .sort((a, b) => a.priceUsd - b.priceUsd || a.sku.localeCompare(b.sku))
      .map((item) => `${item.sku} ($${item.priceUsd.toFixed(2)})`)
      .join(', ');
    const unsupportedSuffix = unsupportedList ? ` Unsupported: ${unsupportedList}.` : '';
    setNotice(
      `Auto import staged ${operations.length} SKU. Skipped: no price ${skippedNoPrice.length}, unsupported price ${skippedNoRule.length}.${unsupportedSuffix}`
    );
    setAutoImportLoading(false);
  };

  const saveConfigDraft = async () => {
    setError(null);
    setNotice(null);
    setSavingConfigs(true);
    try {
      const savedMap = new Map(savedConfigs.map((config) => [config.id, config]));
      const draftMap = new Map(draftConfigs.map((config) => [config.id, config]));

      const deletedIds = savedConfigs.map((config) => config.id).filter((id) => !draftMap.has(id));
      const upserts = draftConfigs.filter((config) => {
        const savedConfig = savedMap.get(config.id);
        return !savedConfig || serializeConfig(savedConfig) !== serializeConfig(config);
      });

      await Promise.all(deletedIds.map((id) => deleteConfig({ variables: { id } })));
      const upsertResults = await Promise.all(
        upserts.map((config) => upsertConfig({ variables: { id: config.id, amount: config.amount } }))
      );

      const updatedMap = new Map(draftConfigs.map((config) => [config.id, config]));
      for (const result of upsertResults) {
        const row = result.data?.upsertLoyaltyCampaignConfig;
        if (row?.id) {
          updatedMap.set(row.id, {
            id: row.id,
            amount: row.amount ?? 0,
            updatedAt: row.updatedAt ?? undefined,
          });
        }
      }

      const nextSavedConfigs = Array.from(updatedMap.values()).sort((a, b) => a.id.localeCompare(b.id));
      setSavedConfigs(nextSavedConfigs);
      setDraftConfigs(nextSavedConfigs);
      setNotice('Loyalty Points Config saved.');
    } catch (e) {
      setError(`Failed to save loyalty config: ${String(e)}`);
    } finally {
      setSavingConfigs(false);
    }
  };

  const saveCardDraft = async (scope: 'all' | 'grouped' | 'single' | 'bundle' = 'all') => {
    setError(null);
    setNotice(null);
    setSavingCards(true);
    try {
      const savedMap = new Map(savedCards.map((card) => [card.id, card]));
      const inScope = (card: DraftLoyaltyCampaignCard): boolean => {
        if (scope === 'all') {
          return true;
        }
        if (scope === 'bundle') {
          return getCardSectionId(card) === 'bundle';
        }
        if ((card.kind ?? 'BUNDLE') !== 'ITEM') {
          return false;
        }
        const isGrouped = (card.groupId ?? '').trim().length > 0;
        return scope === 'grouped' ? isGrouped : !isGrouped;
      };
      const persistedDraftCards = draftCards.filter((card) => !isDraftCardId(card.id) && inScope(card));
      const draftIds = new Set(persistedDraftCards.map((card) => card.id));
      const deletedIds = savedCards
        .filter((card) => inScope(card))
        .map((card) => card.id)
        .filter((id) => !draftIds.has(id));

      await Promise.all(deletedIds.map((id) => deleteCard({ variables: { id } })));

      const createdCards = new Map<string, DraftLoyaltyCampaignCard>();
      for (let index = 0; index < draftCards.length; index += 1) {
        const card = draftCards[index];
        if (isDraftCardId(card.id) && inScope(card)) {
          const rewardsPayload = buildRewardMutationPayload(card);

          const result = await createCard({
            variables: {
              name: card.name.trim(),
              kind: card.kind ?? 'BUNDLE',
              groupId: card.groupId ?? null,
              groupName: card.groupName ?? null,
              groupDisplayOrder: card.groupDisplayOrder ?? null,
              itemDisplayOrder: card.itemDisplayOrder ?? null,
              cost: Number(card.cost),
              itemBonusLoyaltyPoints: card.itemBonusLoyaltyPoints ?? null,
              maxClaims: card.maxClaims ?? 1,
              startDate: card.startDate ?? null,
              endDate: card.endDate ?? null,
              rewards: rewardsPayload,
            },
          });

          const created = result.data?.createLoyaltyCampaignCard;
          if (created?.id) {
            createdCards.set(card.id, {
              ...card,
              ...created,
              id: created.id,
              rewards: [...(card.rewards ?? [])],
              draftRewardInputs: [...(card.draftRewardInputs ?? [])],
            });
          }
        }
      }

      const nextDraftCards = draftCards.map((card, index) => {
        const resolvedCard = createdCards.get(card.id) ?? card;
        return {
          ...resolvedCard,
          displayOrder: index,
        };
      });

      const updates = nextDraftCards
        .filter((card) => !isDraftCardId(card.id))
        .filter((card) => inScope(card))
        .filter((card) => {
          const savedCard = savedMap.get(card.id);
          return !savedCard || serializeComparableCard(savedCard) !== serializeComparableCard(card);
        });

      await Promise.all(
        updates.map((card) => {
          const savedCard = savedMap.get(card.id);
          return updateCard({
            variables: buildCardUpdateVariables(savedCard, card),
          });
        })
      );

      setSavedCards(nextDraftCards);
      setDraftCards(nextDraftCards);
      setNotice(
        scope === 'grouped'
          ? 'Grouped item config saved.'
          : scope === 'single'
            ? 'Single item config saved.'
            : scope === 'bundle'
              ? 'Bundle config saved.'
              : 'Card sections saved.'
      );
    } catch (e) {
      setError(`Failed to save campaign cards: ${String(e)}`);
    } finally {
      setSavingCards(false);
    }
  };

  const onSaveAll = async () => {
    setSavingAll(true);
    try {
      if (isConfigDirty) {
        await saveConfigDraft();
      }
      if (isCardSectionDirty('bundle')) {
        await saveCardDraft('bundle');
      }
      if (isGroupedItemDirty) {
        await saveCardDraft('grouped');
      }
      if (isSingleItemDirty) {
        await saveCardDraft('single');
      }
      if (!isConfigDirty && !isAnyCardDirty) {
        setNotice('No changes to save.');
      }
    } finally {
      setSavingAll(false);
    }
  };

  const openSectionAndScroll = (sectionId: string, beforeScroll?: () => void) => {
    beforeScroll?.();
    window.setTimeout(() => scrollToSection(sectionId), 0);
  };

  const getConfigGroupPriceLabel = (rows: LoyaltyCampaignConfigRow[]): string | null => {
    const uniquePrices = Array.from(
      new Set(rows.map((row) => getSkuPriceUSD(row.id, skuPriceMap)).filter((price): price is number => price !== null))
    ).sort((left, right) => left - right);

    if (uniquePrices.length === 0) {
      return null;
    }

    if (uniquePrices.length === 1) {
      return `$${uniquePrices[0].toFixed(2)}`;
    }

    return `$${uniquePrices[0].toFixed(2)} - $${uniquePrices[uniquePrices.length - 1].toFixed(2)}`;
  };

  const renderCardList = (cards: LoyaltyCampaignCardRow[], sectionId: LoyaltyCampaignCardSectionId) => {
    if (cards.length === 0) {
      return (
        <div
          className="text-muted px-3 py-4"
          style={{ border: '1px dashed #d7dbe0', borderRadius: 16, background: '#fbfcfd' }}
        >
          No cards created yet.
        </div>
      );
    }

    return cards.map((card, localIndex) => {
      const derivedClaimType = getCardClaimType(card.maxClaims, card.claimType);
      const itemCategory =
        (card.kind ?? 'BUNDLE') === 'ITEM' ? getItemCategoryFromReward(card.rewards?.[0]) : null;
      const cardVisualStyle =
        (card.kind ?? 'BUNDLE') === 'BUNDLE'
          ? BUNDLE_CARD_STYLE
          : itemCategory !== null
          ? ITEM_CATEGORY_CARD_STYLES[itemCategory]
          : {
              borderColor: '#e3e7eb',
              background: '#ffffff',
              badgeBackground: '#0f172a',
              badgeText: '#ffffff',
            };
      return (
        <Card
          key={card.id}
          className="mb-3"
          style={{
            borderRadius: 18,
            border: `1px solid ${cardVisualStyle.borderColor}`,
            background: cardVisualStyle.background,
            boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)',
          }}
        >
          <Card.Body style={{ padding: scaled(20) }}>
            <div
              className="d-flex justify-content-between align-items-start flex-wrap mb-3"
              style={{ gap: scaled(16) }}
            >
              <div className="d-flex align-items-start" style={{ gap: 14, minWidth: 0 }}>
                <div
                  style={{
                    width: scaled(42),
                    height: scaled(42),
                    borderRadius: scaled(14),
                    background: cardVisualStyle.badgeBackground,
                    color: cardVisualStyle.badgeText,
                    fontWeight: 700,
                    fontSize: scaled(14),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  #{localIndex + 1}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div className="font-weight-bold" style={{ fontSize: scaled(18), color: '#172033' }}>
                    {card.name}
                  </div>
                  <div className="text-muted" style={{ fontSize: scaled(13), wordBreak: 'break-all' }}>
                    {card.id}
                  </div>
                </div>
              </div>
              <div className="d-flex align-items-center flex-wrap justify-content-end" style={{ gap: 8 }}>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={localIndex === 0 || movingCardId !== null || savingCards}
                  onClick={() => onMoveCardDraft(card.id, 'up', sectionId)}
                >
                  Move Up
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  disabled={localIndex === cards.length - 1 || movingCardId !== null || savingCards}
                  onClick={() => onMoveCardDraft(card.id, 'down', sectionId)}
                >
                  Move Down
                </Button>
                <Button
                  size="sm"
                  variant="light"
                  style={editButtonStyle}
                  disabled={savingCards || movingCardId !== null}
                  onClick={() => onEditCard(card)}
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="light"
                  style={deleteButtonStyle}
                  disabled={savingCards || movingCardId !== null}
                  onClick={() => onDeleteCardDraft(card.id)}
                >
                  {deletingCardId === card.id ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </div>

            <div className="d-flex flex-wrap mb-3" style={{ gap: 10 }}>
              <span style={{ ...metaChipStyle, fontSize: scaled(13), padding: `${scaled(8)}px ${scaled(12)}px` }}>
                Price: {getCardPricingLabel(card)}
              </span>
              {card.groupName ? (
                <span style={{ ...metaChipStyle, fontSize: scaled(13), padding: `${scaled(8)}px ${scaled(12)}px` }}>
                  Group: {card.groupName}
                </span>
              ) : null}
              {Number(card.itemBonusLoyaltyPoints ?? 0) > 0 ? (
                <span style={{ ...metaChipStyle, fontSize: scaled(13), padding: `${scaled(8)}px ${scaled(12)}px` }}>
                  Bonus: +{card.itemBonusLoyaltyPoints} LP
                </span>
              ) : null}
              <span style={{ ...metaChipStyle, fontSize: scaled(13), padding: `${scaled(8)}px ${scaled(12)}px` }}>
                Claim: {getClaimDisplayLabel(card.maxClaims, derivedClaimType)}
              </span>
              <span style={{ ...metaChipStyle, fontSize: scaled(13), padding: `${scaled(8)}px ${scaled(12)}px` }}>
                Start: {card.startDate ? `${moment.utc(card.startDate).format('YYYY-MM-DD HH:mm')} UTC` : 'Always active'}
              </span>
              <span style={{ ...metaChipStyle, fontSize: scaled(13), padding: `${scaled(8)}px ${scaled(12)}px` }}>
                End: {card.endDate ? `${moment.utc(card.endDate).format('YYYY-MM-DD HH:mm')} UTC` : 'No end date'}
              </span>
            </div>

            <div
              className="mb-2"
              style={{ fontSize: scaled(13), fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}
            >
              {getRewardLabel((card.kind ?? 'BUNDLE') as LoyaltyCampaignCardKind)}
            </div>
            <div className="d-flex flex-wrap" style={{ gap: 8 }}>
              {(card.rewards ?? []).length === 0 && <span className="text-muted small">No rewards configured</span>}
              {(card.rewards ?? []).map((reward, rewardIndex) => (
                <span
                  key={`${card.id}_reward_${rewardIndex}`}
                  style={{
                    ...rewardChipStyle,
                    fontSize: scaled(13),
                    padding: `${scaled(8)}px ${scaled(12)}px`,
                  }}
                >
                  {formatSavedReward(reward)}
                </span>
              ))}
            </div>
          </Card.Body>
        </Card>
      );
    });
  };

  const renderLifecycleHeader = (
    title: string,
    count: number,
    variant: keyof typeof LIFECYCLE_SECTION_STYLES = 'active',
    toggle?: { expanded: boolean; onToggle: () => void }
  ) => {
    const lifecycleStyle = LIFECYCLE_SECTION_STYLES[variant];

    return (
    <div
      className="d-flex align-items-center justify-content-between flex-wrap mb-3"
      style={{
        gap: 10,
        padding: `${scaled(12)}px ${scaled(14)}px`,
        borderRadius: scaled(14),
        background: lifecycleStyle.headerBackground,
        border: `1px solid ${lifecycleStyle.headerBorderColor}`,
      }}
    >
      <div>
        <div className="font-weight-bold" style={{ color: '#172033' }}>
          {title}
        </div>
      </div>
      {toggle ? (
        <Button size="sm" variant="outline-secondary" onClick={toggle.onToggle}>
          {toggle.expanded ? 'Hide' : 'Show'}
        </Button>
      ) : null}
    </div>
    );
  };

  const renderLifecycleSection = (
    title: string,
    count: number,
    variant: keyof typeof LIFECYCLE_SECTION_STYLES,
    children: ReactNode,
    toggle?: { expanded: boolean; onToggle: () => void }
  ) => {
    const lifecycleStyle = LIFECYCLE_SECTION_STYLES[variant];

    return (
      <div
        className="mx-auto mb-4"
        style={{
          maxWidth: SECTION_MAX_WIDTH,
          padding: scaled(20),
          borderRadius: scaled(18),
          border: `1px solid ${lifecycleStyle.borderColor}`,
          background: lifecycleStyle.background,
        }}
      >
        {renderLifecycleHeader(title, count, variant, toggle)}
        {children}
      </div>
    );
  };

  const renderGroupBlock = (
    group: LoyaltyCampaignItemGroup,
    sectionId: LoyaltyCampaignCardSectionId,
    keyPrefix = '',
    options?: { enableReorder?: boolean; groupIndex?: number; totalGroups?: number }
  ) => {
    const firstCard = group.cards[0];
    const isCollapsed = Boolean(collapsedGroups[group.id]);
    const canReorderGroup = Boolean(options?.enableReorder && !group.id.startsWith('ungrouped:'));

    return (
      <Card
        key={`${keyPrefix}${group.id}`}
        className="mb-3"
        style={{
          borderRadius: 18,
          border: '1px solid #d7dbe0',
          background: '#fbfcfd',
          boxShadow: '0 6px 18px rgba(15, 23, 42, 0.04)',
          overflow: 'hidden',
        }}
      >
        <Card.Body style={{ padding: scaled(16) }}>
          <div
            className="d-flex align-items-start justify-content-between flex-wrap mb-2"
            style={{ gap: scaled(12) }}
          >
            <div>
              <div className="font-weight-bold" style={{ fontSize: scaled(19), color: '#172033' }}>
                {group.name}
              </div>
              <div className="text-muted" style={{ fontSize: scaled(13) }}>
                {group.cards.length} item(s)
                {group.id.startsWith('ungrouped:') ? '' : ` • ${group.id}`}
              </div>
            </div>
            {!group.id.startsWith('ungrouped:') ? (
              <div className="d-flex align-items-center flex-wrap justify-content-end" style={{ gap: 8 }}>
                {canReorderGroup ? (
                  <>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={(options?.groupIndex ?? 0) === 0 || movingCardId !== null || savingCards}
                      onClick={() => onMoveGroupDraft(group.id, 'up')}
                    >
                      Move Up
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      disabled={
                        (options?.groupIndex ?? 0) === (options?.totalGroups ?? 1) - 1 ||
                        movingCardId !== null ||
                        savingCards
                      }
                      onClick={() => onMoveGroupDraft(group.id, 'down')}
                    >
                      Move Down
                    </Button>
                  </>
                ) : null}
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() =>
                    setCollapsedGroups((prev) => ({
                      ...prev,
                      [group.id]: !prev[group.id],
                    }))
                  }
                >
                  {isCollapsed ? 'Show' : 'Hide'}
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() =>
                    openCreateCardModal('ITEM', undefined, undefined, {
                      groupId: group.id,
                      groupName: group.name,
                      groupDisplayOrder: group.groupDisplayOrder,
                      useSchedule: Boolean(firstCard?.startDate || firstCard?.endDate),
                      startDate: firstCard?.startDate ? new Date(firstCard.startDate) : undefined,
                      endDate: firstCard?.endDate ? new Date(firstCard.endDate) : undefined,
                    })
                  }
                  disabled={savingCards}
                >
                  Add Item
                </Button>
                <Button size="sm" variant="outline-secondary" onClick={() => openEditGroupModal(group)} disabled={savingCards}>
                  Edit Group
                </Button>
                <Button size="sm" variant="outline-danger" onClick={() => deleteGroupDraft(group)} disabled={savingCards}>
                  Delete Group
                </Button>
              </div>
            ) : null}
          </div>

          {!isCollapsed && firstCard ? (
            <div className="d-flex flex-wrap mb-3" style={{ gap: 8 }}>
              <span style={{ ...metaChipStyle, fontSize: scaled(12), padding: `${scaled(6)}px ${scaled(10)}px` }}>
                Group Order: {group.groupDisplayOrder === Number.MAX_SAFE_INTEGER ? '-' : group.groupDisplayOrder}
              </span>
              <span style={{ ...metaChipStyle, fontSize: scaled(12), padding: `${scaled(6)}px ${scaled(10)}px` }}>
                Start:{' '}
                {firstCard.startDate ? `${moment.utc(firstCard.startDate).format('YYYY-MM-DD HH:mm')} UTC` : 'Always active'}
              </span>
              <span style={{ ...metaChipStyle, fontSize: scaled(12), padding: `${scaled(6)}px ${scaled(10)}px` }}>
                End: {firstCard.endDate ? `${moment.utc(firstCard.endDate).format('YYYY-MM-DD HH:mm')} UTC` : 'No end date'}
              </span>
            </div>
          ) : null}

          {!isCollapsed ? (
            <div className="d-flex flex-column" style={{ gap: 8 }}>
            {group.cards.map((card, localIndex) => {
              const derivedClaimType = getCardClaimType(card.maxClaims, card.claimType);

              return (
                <div
                  key={card.id}
                  className="px-3 py-2"
                  style={{
                    border: '1px solid #e2e8f0',
                    borderRadius: 14,
                    background: '#ffffff',
                  }}
                >
                  <div
                    className="d-flex justify-content-between align-items-start flex-wrap mb-2"
                    style={{ gap: 10 }}
                  >
                    <div className="d-flex align-items-start" style={{ gap: 12, minWidth: 0 }}>
                      <div
                        style={{
                          width: scaled(32),
                          height: scaled(32),
                          borderRadius: scaled(10),
                          background: '#2563eb',
                          color: '#ffffff',
                          fontWeight: 700,
                          fontSize: scaled(12),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        #{localIndex + 1}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div className="font-weight-bold" style={{ fontSize: scaled(16), color: '#172033' }}>
                          {card.name}
                        </div>
                        <div className="text-muted" style={{ fontSize: scaled(12), wordBreak: 'break-all' }}>
                          {card.id}
                        </div>
                      </div>
                    </div>
                    <div className="d-flex align-items-center flex-wrap justify-content-end" style={{ gap: 8 }}>
                      {canReorderGroup ? (
                        <>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            disabled={localIndex === 0 || movingCardId !== null || savingCards}
                            onClick={() => onMoveGroupItemDraft(group.id, card.id, 'up')}
                          >
                            Move Up
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            disabled={localIndex === group.cards.length - 1 || movingCardId !== null || savingCards}
                            onClick={() => onMoveGroupItemDraft(group.id, card.id, 'down')}
                          >
                            Move Down
                          </Button>
                        </>
                      ) : null}
                      <Button
                        size="sm"
                        variant="light"
                        style={editButtonStyle}
                        disabled={savingCards || movingCardId !== null}
                        onClick={() => onEditCard(card)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="light"
                        style={deleteButtonStyle}
                        disabled={savingCards || movingCardId !== null}
                        onClick={() => onDeleteCardDraft(card.id)}
                      >
                        {deletingCardId === card.id ? 'Deleting...' : 'Delete'}
                      </Button>
                    </div>
                  </div>

                  <div className="d-flex flex-wrap mb-2" style={{ gap: 8 }}>
                    <span style={{ ...metaChipStyle, fontSize: scaled(12), padding: `${scaled(6)}px ${scaled(10)}px` }}>
                      Price: {getCardPricingLabel(card)}
                    </span>
                    <span style={{ ...metaChipStyle, fontSize: scaled(12), padding: `${scaled(6)}px ${scaled(10)}px` }}>
                      Claim: {getClaimDisplayLabel(card.maxClaims, derivedClaimType)}
                    </span>
                    <span style={{ ...metaChipStyle, fontSize: scaled(12), padding: `${scaled(6)}px ${scaled(10)}px` }}>
                      Item Order: {card.itemDisplayOrder ?? localIndex}
                    </span>
                    {Number(card.itemBonusLoyaltyPoints ?? 0) > 0 ? (
                      <span style={{ ...metaChipStyle, fontSize: scaled(12), padding: `${scaled(6)}px ${scaled(10)}px` }}>
                        Bonus: +{card.itemBonusLoyaltyPoints} LP
                      </span>
                    ) : null}
                  </div>

                  <div
                    className="mb-1"
                    style={{ fontSize: scaled(12), fontWeight: 700, color: '#475569', textTransform: 'uppercase' }}
                  >
                    Item
                  </div>
                  <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                    {(card.rewards ?? []).length === 0 && <span className="text-muted small">No rewards configured</span>}
                    {(card.rewards ?? []).map((reward, rewardIndex) => (
                      <span
                        key={`${card.id}_reward_${rewardIndex}`}
                        style={{
                          ...rewardChipStyle,
                          fontSize: scaled(12),
                          padding: `${scaled(6)}px ${scaled(10)}px`,
                        }}
                      >
                        {formatSavedReward(reward)}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
            </div>
          ) : null}
        </Card.Body>
      </Card>
    );
  };

  const renderItemCategorySection = (
    category: LoyaltyCampaignItemCategory,
    cards: LoyaltyCampaignCardRow[],
    options?: { showAddButton?: boolean; sectionAnchorId?: string }
  ) => {
    const sectionId = `item-${category.toLowerCase()}` as LoyaltyCampaignCardSectionId;
    const categoryVisualStyle = ITEM_CATEGORY_CARD_STYLES[category];
    const showAddButton = options?.showAddButton ?? false;
    const vanityGroups: Record<VanityItemType, LoyaltyCampaignCardRow[]> = {
      VINYL: [],
      FRAME: [],
      STICKER: [],
    };

    if (category === 'VANITY') {
      for (const card of cards) {
        const vanityType = getVanityItemTypeFromReward(card.rewards?.[0]);
        if (vanityType) {
          vanityGroups[vanityType].push(card);
        }
      }
    }

    const groupedCards = buildItemGroups(cards);

    return (
      <div
        key={`${options?.sectionAnchorId ?? 'item'}-${category}`}
        id={options?.sectionAnchorId}
        className="mx-auto mb-4"
        style={{
          maxWidth: SECTION_MAX_WIDTH,
          scrollMarginTop: 72,
          padding: scaled(20),
          borderRadius: scaled(18),
          border: `1px solid ${categoryVisualStyle.borderColor}`,
          background: categoryVisualStyle.background,
        }}
      >
        <div
          className="d-flex align-items-center justify-content-between flex-wrap mb-3"
          style={{
            gap: 10,
            padding: `${scaled(14)}px ${scaled(16)}px`,
            borderRadius: scaled(14),
            background: categoryVisualStyle.headerBackground,
            border: `1px solid ${categoryVisualStyle.headerBorderColor}`,
          }}
        >
          <div>
            <div className="font-weight-bold" style={{ fontSize: 19, color: '#172033' }}>
              {LOYALTY_CAMPAIGN_ITEM_CATEGORY_LABELS[category]}
            </div>
          </div>
          <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
            {showAddButton ? (
              <Button
                size="sm"
                className="px-3"
                style={{
                  ...getItemCategoryBadgeStyle(category),
                  borderColor: 'transparent',
                  fontWeight: 700,
                }}
                onClick={() => openCreateCardModal('ITEM')}
                disabled={savingCards}
              >
                Add Item
              </Button>
            ) : null}
            {isCardSectionDirty(sectionId) ? (
              <span
                style={{
                  ...metaChipStyle,
                  background: '#fff7ed',
                  borderColor: '#fdba74',
                  color: '#b45309',
                }}
              >
                Unsaved changes
              </span>
            ) : null}
            <Button
              variant="primary"
              size="sm"
              onClick={() => saveCardDraft(sectionId === 'bundle' ? 'bundle' : 'single')}
              disabled={!isCardSectionDirty(sectionId) || savingCards}
            >
              {savingCards ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
        {category === 'VANITY' ? (
          (Object.entries(vanityGroups) as Array<[VanityItemType, LoyaltyCampaignCardRow[]]>).map(
            ([vanityType, vanityCards]) => (
              <div key={vanityType} className="mb-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="font-weight-bold">{VANITY_ITEM_LABELS[vanityType]}</div>
                  <div className="text-muted small">{vanityCards.length} card(s)</div>
                </div>
                {vanityCards.length > 0 ? (
                  buildItemGroups(vanityCards).map((group) => renderGroupBlock(group, sectionId, `${vanityType}-`))
                ) : (
                  <div
                    className="text-muted px-3 py-2"
                    style={{ border: '1px dashed #d7dbe0', borderRadius: 10, background: '#fbfcfd' }}
                  >
                    No {VANITY_ITEM_LABELS[vanityType].toLowerCase()} cards yet.
                  </div>
                )}
              </div>
            )
          )
        ) : groupedCards.length > 0 ? (
          groupedCards.map((group) => renderGroupBlock(group, sectionId))
        ) : (
          <div
            className="text-muted px-3 py-2"
            style={{ border: '1px dashed #d7dbe0', borderRadius: 10, background: '#fbfcfd' }}
          >
            No item cards in this category yet.
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <LoadingLogo show={pending} />
      <EditRewardsModal
        isEdit={editingRewardIndex !== null}
        tier={null}
        level={0}
        show={showRewardModal}
        reward={selectedReward}
        handleSave={onSaveRewardModal}
        handleClose={() => {
          setShowRewardModal(false);
          setSelectedReward(null);
          setEditingRewardIndex(null);
        }}
        disableCurrencySpecialEvent
        disableMonthlyBuff
        disableSubscriptionDay={false}
        enableItemLevelEdit
        liveOpsAvailabilities={[ItemAvailability.EARN, ItemAvailability.EARN_EACH_LEVEL]}
        allowedRewardTypes={activeItemRewardTypes}
      />

      <ConfigModal
        show={showConfigModal}
        configModalMode={configModalMode}
        configGroupAmount={configGroupAmount}
        configAmount={configAmount}
        bulkSkusText={bulkSkusText}
        selectedSku={selectedSku}
        offerOptions={offerOptions}
        offerSkuMap={offerSkuMap}
        hasValidConfigAmount={hasValidConfigAmount}
        upsertConfigLoading={savingConfigs}
        onHide={() => {
          setShowConfigModal(false);
          clearConfigForm();
        }}
        onSave={onSaveConfigDraft}
        setConfigAmount={setConfigAmount}
        setBulkSkusText={setBulkSkusText}
        setSelectedSku={setSelectedSku}
      />

      <CardModal
        show={showCardModal}
        editingCardId={editingCardId}
        cardKind={cardKind}
        cardName={cardName}
        cardGroupId={cardGroupId}
        cardGroupName={cardGroupName}
        cardGroupDisplayOrder={cardGroupDisplayOrder}
        cardCost={cardCost}
        cardItemBaseStoreOfferId={cardItemBaseStoreOfferId}
        cardItemSKUPrice={cardItemSKUPrice}
        cardItemBonusLoyaltyPoints={cardItemBonusLoyaltyPoints}
        cardClaimType={cardClaimType}
        cardMaxClaims={cardMaxClaims}
        cardUseSchedule={cardUseSchedule}
        cardStartDate={cardStartDate}
        cardEndDate={cardEndDate}
        cardRewards={cardRewards}
        activeItemCategory={activeItemCategory}
        activeVanityItemType={activeVanityItemType}
        directCurrencyRewardType={directCurrencyRewardType}
        directCurrencyReward={directCurrencyReward}
        directCurrencyAmount={directCurrencyAmount}
        offerOptions={offerOptions}
        offerPriceMap={offerPriceMap}
        offerSkuMap={offerSkuMap}
        skuPriceMap={skuPriceMap}
        hasValidCardInput={hasValidCardInput}
        createCardLoading={false}
        updateCardLoading={false}
        editingRewardIndex={editingRewardIndex}
        onHide={() => setShowCardModal(false)}
        onSaveCard={onSaveCardDraft}
        onOpenAddRewardModal={onOpenAddRewardModal}
        onOpenEditRewardModal={onOpenEditRewardModal}
        onChangeDirectCurrencyAmount={onChangeDirectCurrencyAmount}
        setCardName={setCardName}
        setCardCost={setCardCost}
        setCardItemBaseStoreOfferId={setCardItemBaseStoreOfferId}
        setCardItemSKUPrice={setCardItemSKUPrice}
        setCardItemBonusLoyaltyPoints={setCardItemBonusLoyaltyPoints}
        setCardClaimType={setCardClaimType}
        setCardMaxClaims={setCardMaxClaims}
        setCardUseSchedule={setCardUseSchedule}
        setCardStartDate={setCardStartDate}
        setCardEndDate={setCardEndDate}
        setCardRewards={setCardRewards}
        setVanityItemTypePreset={setVanityItemTypePreset}
        setEditingRewardIndex={setEditingRewardIndex}
        setSelectedReward={setSelectedReward as (value: null) => void}
        setShowRewardModal={setShowRewardModal}
      />

      <Modal show={showGroupModal} onHide={clearGroupForm} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingGroup && isDraftGroupId(editingGroup.originalGroupId) ? 'Create Item Group' : 'Edit Item Group'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingGroup ? (
            <Form>
              {(() => {
                const editingGroupCards = draftCards.filter(
                  (card) => card.kind === 'ITEM' && (card.groupId ?? '').trim() === editingGroup.groupId.trim()
                );
                const isCreatingGroup = isDraftGroupId(editingGroup.originalGroupId);
                return (
                  <>
              <Form.Group>
                <Form.Label>Group ID</Form.Label>
                <Form.Control
                  value={editingGroup.groupId}
                  readOnly
                />
                <Form.Text muted>
                  Generated automatically and used as the shared key for every item in this group.
                </Form.Text>
              </Form.Group>
              <Form.Group>
                <Form.Label>Group Name</Form.Label>
                <Form.Control
                  value={editingGroup.groupName}
                  onChange={(e) => {
                    const nextValue = e.currentTarget.value;
                    setEditingGroup((prev) => (prev ? { ...prev, groupName: nextValue } : prev));
                  }}
                />
              </Form.Group>
              <Form.Group>
                <Form.Check
                  type="switch"
                  id="group-use-schedule"
                  label="Use group schedule"
                  checked={editingGroup.useSchedule}
                  onChange={(e) => {
                    const nextChecked = e.currentTarget.checked;
                    setEditingGroup((prev) =>
                      prev
                        ? {
                            ...prev,
                            useSchedule: nextChecked,
                            endDate: nextChecked ? prev.endDate : null,
                          }
                        : prev
                    );
                  }}
                />
              </Form.Group>
              {editingGroup.useSchedule ? (
                <>
                  <Form.Group>
                    <Form.Label>Start Date (UTC)</Form.Label>
                    <DateTimePicker
                      dateFormat="YYYY-MM-DD"
                      timeFormat="HH:mm"
                      utc={true}
                      value={editingGroup.startDate ? moment.utc(editingGroup.startDate) : undefined}
                      onChange={(value) =>
                        setEditingGroup((prev) =>
                          prev ? { ...prev, startDate: moment.isMoment(value) ? value.toDate() : null } : prev
                        )
                      }
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>End Date (UTC)</Form.Label>
                    <DateTimePicker
                      dateFormat="YYYY-MM-DD"
                      timeFormat="HH:mm"
                      utc={true}
                      value={editingGroup.endDate ? moment.utc(editingGroup.endDate) : undefined}
                      onChange={(value) =>
                        setEditingGroup((prev) =>
                          prev ? { ...prev, endDate: moment.isMoment(value) ? value.toDate() : null } : prev
                        )
                      }
                    />
                  </Form.Group>
                </>
              ) : null}
              <div className="mt-4">
                <div className="d-flex justify-content-between align-items-center mb-2" style={{ gap: 12 }}>
                  <div>
                    <div className="font-weight-bold">Group Items</div>
                    <div className="text-muted" style={{ fontSize: 13 }}>
                      Add item from the same item form used in Single Item Config.
                    </div>
                  </div>
                  <Button
                    variant="outline-primary"
                    onClick={() =>
                      openCreateCardModal('ITEM', undefined, undefined, {
                        groupId: editingGroup.groupId,
                        groupName: editingGroup.groupName,
                        groupDisplayOrder:
                          editingGroup.groupDisplayOrder.trim() === ''
                            ? null
                            : Number(editingGroup.groupDisplayOrder),
                        useSchedule: editingGroup.useSchedule,
                        startDate: editingGroup.startDate,
                        endDate: editingGroup.endDate,
                      })
                    }
                  >
                    Add Item
                  </Button>
                </div>
                {editingGroupCards.length === 0 ? (
                  <div
                    className="text-muted px-3 py-3"
                    style={{ border: '1px dashed #d7dbe0', borderRadius: 10, background: '#fbfcfd' }}
                  >
                    No items in this group yet.
                  </div>
                ) : (
                  editingGroupCards.map((card) => {
                    const firstReward = card.draftRewardInputs?.[0];
                    const category = firstReward ? getItemCategoryFromRewardForm(firstReward) : 'OTHER';
                    const vanityType = firstReward ? getVanityItemTypeFromRewardForm(firstReward) : null;
                    return (
                      <div
                        key={card.id}
                        className="d-flex justify-content-between align-items-start px-3 py-3 mb-2"
                        style={{ border: '1px solid #e2e8f0', borderRadius: 12, background: '#ffffff', gap: 12 }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div className="font-weight-bold">{card.name}</div>
                          <div className="text-muted" style={{ fontSize: 13 }}>
                            {LOYALTY_CAMPAIGN_ITEM_CATEGORY_LABELS[category]}
                            {vanityType ? ` • ${VANITY_ITEM_LABELS[vanityType]}` : ''}
                            {firstReward?.itemId ? ` • ${firstReward.itemId}` : ''}
                          </div>
                        </div>
                        <div className="d-flex" style={{ gap: 8 }}>
                          <Button variant="outline-secondary" size="sm" onClick={() => onEditCard(card)}>
                            Edit
                          </Button>
                          <Button variant="outline-danger" size="sm" onClick={() => onDeleteCardDraft(card.id)}>
                            Remove
                          </Button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
                  </>
                );
              })()}
            </Form>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={clearGroupForm}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={saveGroupDraft}
            disabled={
              Boolean(editingGroup && isDraftGroupId(editingGroup.originalGroupId)) &&
              draftCards.filter(
                (card) => card.kind === 'ITEM' && (card.groupId ?? '').trim() === (editingGroup?.groupId ?? '').trim()
              ).length === 0
            }
          >
            {editingGroup && isDraftGroupId(editingGroup.originalGroupId) ? 'Add Group' : 'Save Group'}
          </Button>
        </Modal.Footer>
      </Modal>

      <div
        style={{
          ...pageShellStyle,
          paddingLeft: scaled(16),
          paddingRight: scaled(16),
          paddingBottom: scaled(120),
        }}
      >
        {error && <Alert variant="danger">{error}</Alert>}
        {notice && <Alert variant="success">{notice}</Alert>}

        <Card className="mb-4" id="loyalty-config-section" style={{ ...sectionCardStyle, borderRadius: scaled(20) }}>
          <Card.Body>
            <div className="mx-auto" style={{ maxWidth: PAGE_MAX_WIDTH }}>
              <div style={sectionHeaderStyle}>
                <div>
                  <div style={sectionEyebrowStyle}>Pricing Rules</div>
                  <Card.Title className="mb-2 mt-3" style={{ fontSize: scaled(28), color: '#172033' }}>
                    Loyalty Points Config
                  </Card.Title>
                </div>
                <div className="d-flex align-items-center flex-wrap justify-content-end" style={{ gap: 8 }}>
                  <span
                    style={{
                      ...metaChipStyle,
                      background: isConfigDirty ? '#fff7ed' : '#f0fdf4',
                      borderColor: isConfigDirty ? '#fdba74' : '#bbf7d0',
                      color: isConfigDirty ? '#b45309' : '#166534',
                    }}
                  >
                    {isConfigDirty ? 'Unsaved changes' : 'Saved'}
                  </span>
                  <span style={metaChipStyle}>{configGroups.length} group(s)</span>
                  <span style={metaChipStyle}>{draftConfigs.length} SKU</span>
                </div>
              </div>

              <div className="d-flex flex-wrap justify-content-between align-items-center mb-3" style={{ gap: 12 }}>
                <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="px-3"
                    onClick={onAutoImportConfigByPrice}
                    disabled={savingConfigs || autoImportLoading}
                  >
                    {autoImportLoading ? 'Importing...' : 'Auto Import by Price'}
                  </Button>
                  <Button
                    variant="success"
                    size="sm"
                    className="px-3"
                    style={{ borderColor: '#22b573', backgroundColor: '#22b573' }}
                    onClick={openCreateConfigModal}
                    disabled={savingConfigs || autoImportLoading}
                  >
                    Add Config
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    className="px-3"
                    onClick={saveConfigDraft}
                    disabled={!isConfigDirty || savingConfigs}
                  >
                    {savingConfigs ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>

              {configGroups.length === 0 && <div className="text-center text-muted py-3">No SKU config yet.</div>}
              {configGroups.map((group) => {
                const groupPriceLabel = getConfigGroupPriceLabel(group.rows);

                return (
                  <div
                    key={`group_${group.amount}`}
                    className="mb-3 p-3"
                    style={{
                      border: '1px solid #e5e8ec',
                      borderRadius: scaled(16),
                      background: '#fbfcfe',
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center flex-wrap mb-2" style={{ gap: 12 }}>
                      <div>
                        <div className="font-weight-bold" style={{ color: '#172033' }}>
                          {group.amount} Loyalty Points{groupPriceLabel ? ` (${groupPriceLabel})` : ''}
                        </div>
                        <div className="text-muted small">{group.rows.length} SKU in this group</div>
                      </div>
                      <div className="d-flex justify-content-end flex-wrap" style={{ gap: 8 }}>
                        <Button
                          size="sm"
                          variant="success"
                          style={{ whiteSpace: 'nowrap', backgroundColor: '#22b573', borderColor: '#22b573' }}
                          disabled={savingConfigs}
                          onClick={() => onAddSkuToGroup(group.amount)}
                        >
                          Add SKU
                        </Button>
                        <Button
                          size="sm"
                          variant="primary"
                          style={{ backgroundColor: '#3f7ee8', borderColor: '#3f7ee8' }}
                          disabled={savingConfigs}
                          onClick={() => onEditGroupAmount(group.rows)}
                        >
                          Edit Points
                        </Button>
                        <Button size="sm" variant="outline-danger" disabled={savingConfigs} onClick={() => onDeleteGroupDraft(group.rows)}>
                          {deletingConfigId === `group:${group.amount}` ? 'Deleting...' : 'Delete Group'}
                        </Button>
                      </div>
                    </div>
                    <Row className="g-1">
                      {group.rows.map((config) => {
                        const skuPrice = getSkuPriceUSD(config.id, skuPriceMap);
                        const matchedOfferId = findOfferIdBySku(config.id, offerSkuMap);
                        const resolvedOfferId =
                          matchedOfferId && !SHOULD_SKIP_GENERIC_OFFER_LABEL(matchedOfferId) ? matchedOfferId : null;
                        const matchedOffer = resolvedOfferId ? offerById[resolvedOfferId] ?? null : null;
                        return (
                          <Col md={6} key={config.id}>
                            <div
                              className="d-flex justify-content-between align-items-center px-3 py-2"
                              style={{
                                borderRadius: 12,
                                background: '#fff',
                                border: '1px solid #edf1f5',
                                gap: 8,
                              }}
                            >
                              <div style={{ overflow: 'visible', flex: 1, minWidth: 0 }}>
                                <div
                                  title={matchedOffer ? `${formatOfferOptionLabel(matchedOffer)}\n${config.id}` : config.id}
                                  style={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    fontWeight: 600,
                                    fontSize: scaled(12),
                                    lineHeight: 1.35,
                                  }}
                                >
                                  {matchedOffer
                                    ? formatOfferOptionLabel(matchedOffer)
                                    : `${config.id}${skuPrice !== null ? ` ($${skuPrice.toFixed(2)})` : ''}`}
                                </div>
                                {matchedOffer ? (
                                  <div
                                    className="small text-muted text-monospace"
                                    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                    title={config.id}
                                  >
                                    {config.id}
                                    {skuPrice !== null ? ` ($${skuPrice.toFixed(2)})` : ''}
                                  </div>
                                ) : null}
                              </div>
                              <div className="text-right ml-2">
                                <Button size="sm" variant="outline-danger" disabled={savingConfigs} onClick={() => onDeleteConfigDraft(config.id)}>
                                  {deletingConfigId === config.id ? 'Deleting...' : 'Delete'}
                                </Button>
                              </div>
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  </div>
                );
              })}
            </div>
          </Card.Body>
        </Card>

        {showBundleConfig ? (
          <Card
            className="mb-4"
            id="bundle-cards-section"
            style={{ ...sectionCardStyle, borderRadius: scaled(20) }}
          >
            <Card.Body>
              <div className="mx-auto" style={{ maxWidth: SECTION_MAX_WIDTH }}>
                <div style={sectionHeaderStyle}>
                  <div>
                    <Card.Title className="mb-2" style={{ fontSize: scaled(28), color: '#172033' }}>
                      Bundle Config
                    </Card.Title>
                  </div>
                  <div className="d-flex align-items-center flex-wrap justify-content-end" style={{ gap: 8 }}>
                    <span
                      style={{
                        ...metaChipStyle,
                        background: isCardSectionDirty('bundle') ? '#fff7ed' : '#f0fdf4',
                        borderColor: isCardSectionDirty('bundle') ? '#fdba74' : '#bbf7d0',
                        color: isCardSectionDirty('bundle') ? '#b45309' : '#166534',
                      }}
                    >
                      {isCardSectionDirty('bundle') ? 'Unsaved changes' : 'Saved'}
                    </span>
                    <span style={metaChipStyle}>{bundleCardGroupsByState.active.length} active</span>
                    <span style={metaChipStyle}>{bundleCardGroupsByState.scheduled.length} scheduled</span>
                    <span style={metaChipStyle}>{bundleCardGroupsByState.ended.length} ended</span>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="px-3"
                      onClick={() => setShowBundleConfig(false)}
                    >
                      Hide
                    </Button>
                  </div>
                </div>
                <div className="d-flex flex-wrap justify-content-end mb-3" style={{ gap: 8 }}>
                  <Button
                    variant="success"
                    size="sm"
                    className="px-3"
                    style={{ borderColor: '#22b573', backgroundColor: '#22b573' }}
                    onClick={() => openCreateCardModal('BUNDLE')}
                    disabled={savingCards}
                  >
                    Add Bundle Card
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    className="px-3"
                    onClick={() => saveCardDraft('bundle')}
                    disabled={!isCardSectionDirty('bundle') || savingCards}
                  >
                    {savingCards ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>
              {renderLifecycleSection(
                'Active',
                bundleCardGroupsByState.active.length,
                'active',
                bundleCardGroupsByState.active.length > 0 ? (
                  renderCardList(bundleCardGroupsByState.active, 'bundle')
                ) : (
                  <div
                    className="text-muted px-3 py-2"
                    style={{ border: '1px dashed #d7dbe0', borderRadius: 10, background: '#fbfcfd' }}
                  >
                    No active bundle cards.
                  </div>
                )
              )}

              {bundleCardGroupsByState.scheduled.length > 0 ? (
                renderLifecycleSection(
                  'Scheduled',
                  bundleCardGroupsByState.scheduled.length,
                  'scheduled',
                  renderCardList(bundleCardGroupsByState.scheduled, 'bundle')
                )
              ) : null}

              {bundleCardGroupsByState.ended.length > 0 ? (
                renderLifecycleSection(
                  'Ended Campaigns',
                  bundleCardGroupsByState.ended.length,
                  'ended',
                  showEndedBundleCards ? renderCardList(bundleCardGroupsByState.ended, 'bundle') : null,
                  {
                    expanded: showEndedBundleCards,
                    onToggle: () => setShowEndedBundleCards((prev) => !prev),
                  }
                )
              ) : null}
            </Card.Body>
          </Card>
        ) : null}

        <Card className="mb-4" id="grouped-item-cards-section" style={{ ...sectionCardStyle, borderRadius: scaled(20) }}>
          <Card.Body>
            <div className="mx-auto mb-4" style={{ maxWidth: SECTION_MAX_WIDTH }}>
              <div style={sectionHeaderStyle}>
                <div>
                  <Card.Title className="mb-2" style={{ fontSize: scaled(28), color: '#172033' }}>
                    Grouped Item Config
                  </Card.Title>
                </div>
                <div className="d-flex align-items-center flex-wrap justify-content-end" style={{ gap: 8 }}>
                  <span
                    style={{
                      ...metaChipStyle,
                      background: isGroupedItemDirty ? '#fff7ed' : '#f0fdf4',
                      borderColor: isGroupedItemDirty ? '#fdba74' : '#bbf7d0',
                      color: isGroupedItemDirty ? '#b45309' : '#166534',
                    }}
                  >
                    {isGroupedItemDirty ? 'Unsaved grouped items' : 'Saved'}
                  </span>
                  <span style={metaChipStyle}>{groupedItemCardsByState.active.length} active groups</span>
                  <span style={metaChipStyle}>{groupedItemCardsByState.scheduled.length} scheduled groups</span>
                  <span style={metaChipStyle}>{groupedItemCardsByState.ended.length} ended groups</span>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="px-3"
                    onClick={() => setShowGroupedItemConfig((prev) => !prev)}
                  >
                    {showGroupedItemConfig ? 'Hide' : 'Show'}
                  </Button>
                </div>
              </div>
              {showGroupedItemConfig ? (
                <div className="d-flex flex-wrap justify-content-end mb-3" style={{ gap: 8 }}>
                <Button
                  variant="success"
                  size="sm"
                  className="px-3"
                  style={{ borderColor: '#22b573', backgroundColor: '#22b573' }}
                  onClick={openCreateGroupModal}
                  disabled={savingCards}
                >
                  Create Group
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  className="px-3"
                  onClick={() => saveCardDraft('grouped')}
                  disabled={!isGroupedItemDirty || savingCards}
                >
                  {savingCards ? 'Saving...' : 'Save'}
                </Button>
                </div>
              ) : null}
            </div>
            {showGroupedItemConfig
              ? renderLifecycleSection(
              'Active Groups',
              groupedItemCardsByState.active.length,
              'active',
              groupedItemCardsByState.active.length > 0 ? (
                groupedItemCardsByState.active.map((group, index) =>
                  renderGroupBlock(group, getCardSectionId(group.cards[0]), 'active-', {
                    enableReorder: true,
                    groupIndex: index,
                    totalGroups: groupedItemCardsByState.active.length,
                  })
                )
              ) : (
                <div className="text-muted px-3 py-2" style={{ border: '1px dashed #d7dbe0', borderRadius: 10, background: '#fbfcfd' }}>
                  No active grouped item config yet.
                </div>
              )
                )
              : null}

            {showGroupedItemConfig && groupedItemCardsByState.scheduled.length > 0 ? (
              renderLifecycleSection(
                'Scheduled Groups',
                groupedItemCardsByState.scheduled.length,
                'scheduled',
                groupedItemCardsByState.scheduled.map((group, index) =>
                  renderGroupBlock(group, getCardSectionId(group.cards[0]), 'scheduled-', {
                    enableReorder: true,
                    groupIndex: index,
                    totalGroups: groupedItemCardsByState.scheduled.length,
                  })
                )
              )
            ) : null}

            {showGroupedItemConfig && groupedItemCardsByState.ended.length > 0 ? (
              renderLifecycleSection(
                'Ended Groups',
                groupedItemCardsByState.ended.length,
                'ended',
                showEndedItemCards
                  ? groupedItemCardsByState.ended.map((group, index) =>
                      renderGroupBlock(group, getCardSectionId(group.cards[0]), 'ended-', {
                        enableReorder: true,
                        groupIndex: index,
                        totalGroups: groupedItemCardsByState.ended.length,
                      })
                    )
                  : null,
                {
                  expanded: showEndedItemCards,
                  onToggle: () => setShowEndedItemCards((prev) => !prev),
                }
              )
            ) : null}
          </Card.Body>
        </Card>

        {showSingleItemConfig ? (
          <Card className="mb-4" id="single-item-cards-section" style={{ ...sectionCardStyle, borderRadius: scaled(20) }}>
            <Card.Body>
              <div className="mx-auto mb-4" style={{ maxWidth: SECTION_MAX_WIDTH }}>
                <div style={sectionHeaderStyle}>
                  <div>
                    <Card.Title className="mb-2" style={{ fontSize: scaled(28), color: '#172033' }}>
                      Single Item Config
                    </Card.Title>
                  </div>
                  <div className="d-flex align-items-center flex-wrap justify-content-end" style={{ gap: 8 }}>
                    <span
                      style={{
                        ...metaChipStyle,
                        background: isSingleItemDirty ? '#fff7ed' : '#f0fdf4',
                        borderColor: isSingleItemDirty ? '#fdba74' : '#bbf7d0',
                        color: isSingleItemDirty ? '#b45309' : '#166534',
                      }}
                    >
                      {isSingleItemDirty ? 'Unsaved item sections' : 'Saved'}
                    </span>
                    <span style={metaChipStyle}>
                      {Object.values(ungroupedItemCardsByState.active).flat().length} active
                    </span>
                    <span style={metaChipStyle}>
                      {Object.values(ungroupedItemCardsByState.scheduled).flat().length} scheduled
                    </span>
                    <span style={metaChipStyle}>
                      {Object.values(ungroupedItemCardsByState.ended).flat().length} ended
                    </span>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="px-3"
                      onClick={() => setShowSingleItemConfig(false)}
                    >
                      Hide
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      className="px-3"
                      onClick={() => saveCardDraft('single')}
                      disabled={!isSingleItemDirty || savingCards}
                    >
                      {savingCards ? 'Saving...' : 'Save'}
                    </Button>
                  </div>
                </div>
              </div>
              {ITEM_CATEGORY_ORDER.map((category) =>
                renderItemCategorySection(category, ungroupedItemCardsByState.active[category], {
                  showAddButton: true,
                  sectionAnchorId: `item-category-${category.toLowerCase()}`,
                })
              )}

              {Object.values(ungroupedItemCardsByState.scheduled).some((cards) => cards.length > 0) ? (
                renderLifecycleSection(
                  'Scheduled',
                  Object.values(ungroupedItemCardsByState.scheduled).flat().length,
                  'scheduled',
                  ITEM_CATEGORY_ORDER
                    .filter((category) => ungroupedItemCardsByState.scheduled[category].length > 0)
                    .map((category) =>
                      renderItemCategorySection(category, ungroupedItemCardsByState.scheduled[category])
                    )
                )
              ) : null}

              {Object.values(ungroupedItemCardsByState.ended).some((cards) => cards.length > 0) ? (
                renderLifecycleSection(
                  'Ended Campaigns',
                  Object.values(ungroupedItemCardsByState.ended).flat().length,
                  'ended',
                  showEndedItemCards
                    ? ITEM_CATEGORY_ORDER
                        .filter((category) => ungroupedItemCardsByState.ended[category].length > 0)
                        .map((category) =>
                          renderItemCategorySection(category, ungroupedItemCardsByState.ended[category])
                        )
                    : null,
                  {
                    expanded: showEndedItemCards,
                    onToggle: () => setShowEndedItemCards((prev) => !prev),
                  }
                )
              ) : null}
            </Card.Body>
          </Card>
        ) : null}

      <div
        style={{
          position: 'fixed',
          right: scaled(24),
          bottom: scaled(24),
          zIndex: 30,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: scaled(10),
        }}
      >
        <Card
          style={{
            ...sectionCardStyle,
            width: scaled(260),
            maxWidth: 'calc(100vw - 32px)',
            borderRadius: scaled(18),
            boxShadow: '0 12px 28px rgba(15, 23, 42, 0.12)',
          }}
        >
          <Card.Body style={{ padding: scaled(14) }}>
            <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 8 }}>
              <div className="font-weight-bold" style={{ color: '#172033', fontSize: scaled(14) }}>
                Quick Navigation
              </div>
              <Button size="sm" variant="outline-secondary" onClick={() => setShowQuickNavigation((prev) => !prev)}>
                {showQuickNavigation ? 'Hide' : 'Show'}
              </Button>
            </div>
            {showQuickNavigation && (
              <div className="d-flex flex-column mt-3" style={{ gap: 8 }}>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  className="text-left"
                  onClick={() => scrollToSection('loyalty-config-section')}
                >
                  Loyalty Points Config
                </Button>
                {showBundleConfig ? (
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className="text-left"
                    onClick={() => openSectionAndScroll('bundle-cards-section')}
                  >
                    Bundle Config
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  variant="outline-secondary"
                  className="text-left"
                  onClick={() =>
                    openSectionAndScroll('grouped-item-cards-section', () => {
                      setShowGroupedItemConfig(true);
                    })
                  }
                >
                  Grouped Item Config
                </Button>
                {showSingleItemConfig ? (
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    className="text-left"
                    onClick={() => openSectionAndScroll('single-item-cards-section')}
                  >
                    Single Item Config
                  </Button>
                ) : null}
                {showSingleItemConfig ? (
                  ITEM_CATEGORY_ORDER.map((category) => (
                    <Button
                      key={`nav_${category}`}
                      size="sm"
                      variant="outline-secondary"
                      className="text-left"
                      onClick={() =>
                        openSectionAndScroll(`item-category-${category.toLowerCase()}`, () => {
                          setShowSingleItemConfig(true);
                        })
                      }
                    >
                      {LOYALTY_CAMPAIGN_ITEM_CATEGORY_LABELS[category]}
                    </Button>
                  ))
                ) : null}
              </div>
            )}
          </Card.Body>
        </Card>

        <div
          style={{
            ...actionDockStyle,
            position: 'relative',
            right: 'auto',
            bottom: 'auto',
            borderRadius: scaled(20),
            padding: `${scaled(12)}px ${scaled(14)}px`,
            gap: scaled(12),
          }}
        >
        <div
          style={{
            padding: '8px 12px',
            borderRadius: 999,
            background: hasAnyDraftChanges ? '#fff7ed' : '#f0fdf4',
            border: `1px solid ${hasAnyDraftChanges ? '#fdba74' : '#bbf7d0'}`,
            fontSize: scaled(13),
            color: hasAnyDraftChanges ? '#c05621' : '#166534',
            fontWeight: 600,
          }}
        >
          {hasAnyDraftChanges ? 'Unsaved draft changes' : 'All changes saved'}
        </div>
        <Button
          variant="primary"
          size="lg"
          onClick={onSaveAll}
          disabled={!hasAnyDraftChanges || savingAll || savingConfigs || savingCards}
          style={{
            minWidth: scaled(140),
            borderRadius: 999,
            boxShadow: '0 12px 28px rgba(63, 126, 232, 0.28)',
            padding: `${scaled(12)}px ${scaled(24)}px`,
          }}
        >
          {savingAll || savingConfigs || savingCards ? 'Saving...' : 'Save All'}
        </Button>
        </div>
      </div>
      </div>
    </>
  );
};

export default LoyaltyCampaign;
